// src/views/MenageSuiviView.jsx — REFONTE « Pilotage de l'équipe » (maquette
// validée par Dimitri le 2026-08-10) par-dessus la refonte du 2026-08-06.
//
// STRUCTURE : DEUX COLONNES. À gauche (~65 %) le TRAVAIL DU JOUR ; à droite
// (235 px, fond ciel très pâle) le CONTEXTE. Les pastilles numérotées 1/2/3 ont
// disparu : la hiérarchie vient de la place qu'occupe une chose, pas d'un
// numéro qui la décrète. Sur mobile, la colonne de droite passe SOUS la
// principale (elle informe, elle ne commande pas).
//
// COLONNE PRINCIPALE, dans l'ordre de lecture :
//   · BILAN DE LA VEILLE — une ligne. SEULE touche de vert de la page, et
//     seulement quand tout est validé : ici le vert veut dire « rien à faire ».
//   · LES CARTES DU JOUR — le fond de chaque carte dit son état (sans agent /
//     normale / en retard). Au-delà de 4 ménages, les cartes SANS DÉCISION se
//     replient en lignes compactes ; ce qui appelle une action reste toujours
//     déplié (cf. repartirCartes dans lib/menage-suivi-pilotage.js) — règle
//     conservée pour la liste de l'agent, la frise n'en a plus besoin.
//   · DEMAIN — une bande cliquable, qui ne porte que deux nombres.
//
// COLONNE DE DROITE : demandes voyageurs (ce qui presse), stock, semaine,
// propreté, puis le lien discret vers les règles d'assignation.
//
// NAVIGATION PAR JOUR (nouveau) : flèches ‹ › dans le bandeau. L'écran n'est
// plus cloué sur aujourd'hui. Conséquences tenues explicitement :
//   — l'ALERTE « check-in dépassé » ne vaut que pour AUJOURD'HUI (sur un jour
//     passé elle serait vraie par construction, sur un jour futur toujours
//     fausse) ; sur un jour PASSÉ, un ménage non validé est signalé comme tel,
//     ce qui est le même fait dit avec le mot juste ;
//   — les droits de terrain (fenêtre d'incident, ouverture de checklist)
//     restent calculés sur la VRAIE date du jour, jamais sur le jour affiché :
//     naviguer, c'est regarder, pas déverrouiller.
//
// ALERTES ADR-230/231 PRÉSERVÉES · périmètre de lecture = buildRunsProviderFilter
// (pool + orphelins, B2), la RLS faisant foi côté serveur · pas de tarif, aucun
// champ financier du guichet · ANTI DOUBLE-DRAIN PHOTO : un seul modal à la fois.

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import FeuilleConfirmation from '../components/FeuilleConfirmation'
import { supabase } from "../supabase"
import useEchapPourFermer from "../lib/useEchapPourFermer"
import { chargerRegles } from "../lib/menage-regles-api"
import { joursDAttente } from "../lib/incidents-attente"
import ReglesAssignationSection from "./menage/ReglesAssignationSection"
import { DS } from "../lib/ui"
import CarteTitrePage, { boutonCarteTitre } from "../components/CarteTitrePage"
import { resolveLogTime, getEffectiveRunStatus, isRunOpenForMenageToday } from "../lib/menage-status-helpers"
import { isWithinIncidentWindow } from "../lib/incidents-window"
import { aggregateSuivi } from "../lib/menage-suivi-aggregate"
import {
  decalerJour, jourLabelCourt, reperJour, bilanVeille, apercuLendemain,
  indexerDemandesHoraires, ancienneteLisible, demandeLaPlusAncienne,
} from "../lib/menage-suivi-pilotage"
import FriseJournee, { ModaleIntervention, ModaleDuree } from "./menage/FriseJournee"
import { RecapAgent, RecapSansAgent } from "./menage/RecapsSemaine"
import { construireBlocs, arriveeAffichee, SANS_AGENT, detecterConflitsParAgent } from "../lib/menage-frise"
import { resumeJournee } from "../lib/resume-journee"
import { fetchOrchestratorPoolIncidents, listIncidents, normalizeStatus, STATUS as STATUS_INCIDENTS } from "../lib/incidents-api"
import { minutesDeHeure, formaterHeure, formaterHeureCourte, formaterDuree } from "../lib/menage-frise"
import {
  accepterRecalage, fixerHeureRun, fixerDureeRun, chargerInterventions, creerIntervention,
  majIntervention, supprimerIntervention, heureCourte,
} from "../lib/menage-frise-api"
import { fixerDureeDefautLogement } from "../lib/menage-duree-api"
import { produitsSousSeuil } from "../lib/stock-alerte"
import {
  versLaDemande, cheminRetour, libelleRetour, pourOnglet, ONGLET_FRISE,
} from "../lib/orchestrateur-navigation"
import { computeVisibleEmails, computeAssignOptions, buildRunsProviderFilter, voitToutLeParc, todayParisIso, nowParisHHMM } from "./MenageTodayView"
import { firstNameFromEmail, libelleDepuisEmail, initialsFromEmail } from "./menage/RunCard"
import { fetchLogInfo, fetchLogInfos } from "../lib/menage-log-info-cache"
import { fetchPoolPrenoms } from "../lib/pool-prenoms"
import { fetchMenageAvis } from "../lib/menage-avis-api"
import { buildAvisIndicatorCards } from "./menage/MenageAvisIndicator"
// CHANTIER 42 Phase 4 (ADR-302) — rédaction des consignes internes. Section
// REPLIABLE en bas de l'écran de pilotage (pas de nouvel onglet). Le composant
// est PARTAGÉ avec l'onglet Checklists admin ; ici, `canWriteTransverse=false`
// (la forme transverse échappe au cloisonnement par logement → ADMIN ONLY).
import ConsignesEditor from "./menage/ConsignesEditor"
import { STATUS, createIncident } from "../lib/incidents-api"
import IncidentModal from "../modals/IncidentModal"
import ChecklistRunModal from "../modals/ChecklistRunModal"
// LOT FRISE CONTRÔLE — batch UNIQUE des contrôles des runs affichés (RLS
// cloisonne : admin tout, orchestrateur pool, proprio 0 ligne) + panneau
// détail LECTURE SEULE ouvert par la pastille œil.
import { chargerControlesParRun } from "../lib/controles-suivi-api"
// PANNEAU DU MÉNAGE (maquette 2026-08-19) — défauts de contrôleur par
// logement (RLS directe, batch vue) + désignation par-ménage (action serveur
// existante controle_designer).
import { chargerControleurDefauts, designerControleurRun } from "../lib/panneau-menage-api"
import { fetchManques, reglerManque } from "../lib/stock-signalements-api"
import { nomProduit } from "../lib/consommables-helpers"
import SectionControleursDefaut from "./menage/SectionControleursDefaut"
import PanneauDetailControle from "../components/PanneauDetailControle"
import { useMenagePhotoUploadQueue } from "../lib/use-photo-upload-queue"
import { commitUploadedPhoto } from "../lib/menage-photo-commit"
import useEtroit from "../lib/useEtroit"
import { ClipboardX,
  AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, CircleCheck, Package, Star, Plus, ArrowLeft,
  Check, Hourglass, LogOut, LogIn, X, Users, Eye,
} from "lucide-react"

// ── PALETTE DE LA REFONTE (maquette validée — AUCUNE autre couleur) ──────────
// Charte 2026 bleu-gris ; le terracotta est RÉSERVÉ aux alertes, le vert au
// seul bilan de la veille quand il n'y a rien à faire.
const C = {
  gris:        '#3d5166',   // bandeau, boutons pleins, carte propreté
  fonce:       '#2e3d4d',   // titres
  ciel:        '#87b8e6',   // badges d'état, carte « demandes voyageurs »
  cielPale:    '#e4f0fb',   // pastilles d'initiales
  cielTexte:   '#1c2833',   // texte sur ciel
  colonne:     '#f4f9fd',   // fond colonne droite ET fond des cartes normales
  carteBord:   '#d5e3ee',   // bordure des cartes normales
  demain:      '#e8f2fa',   // bande « demain »
  demainAlerte:'#a8412b',   // « 2 sans agent » dans la bande demain
  // Orphelin (« à assigner »)
  orphFond:    '#fdf4f0',
  orphBord:    '#f0d5c9',
  orphAccent:  '#c1512d',
  // Retard
  retardBord:  '#b8412b',   // plus soutenu que l'orphelin : il prime
  retardFond:  '#fbe9e5',
  retardTexte: '#8f2f1d',
  // Compteurs du bandeau, sur fond #3d5166
  cptAssigner: '#f0b4a0',
  cptRetard:   '#f5a892',
  // Bilan de la veille — LE SEUL VERT DE LA PAGE
  vertFond:    '#f0f7f3',
  vertTexte:   '#2c7a5c',
  ambre:       '#EF9F27',   // étoiles avis
  zeroGris:    '#c9ccd4',
}

/** « Jeudi 14 août » — la date longue du jour REGARDÉ (sous-titre v2). */
function jourLabelLong(iso) {
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  // RÈGLE DES DATES (2026-08-22) : « 22 août » — jamais le jour de semaine.
  const t = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
  return t.charAt(0).toUpperCase() + t.slice(1)
}

const LARGEUR_COLONNE = 186

// Feuille de style locale : les media queries et `position:sticky` ne
// s'expriment pas en style inline, et le passage de la colonne SOUS la
// principale sur mobile est une exigence, pas un agrément.
//
// ⚠️ COLONNE COLLANTE. Elle était en `align-self:stretch` : avec quinze lignes
// dans la frise, elle défilait avec la page et disparaissait — au moment précis
// où on a le plus besoin de savoir qui est libre et ce qui manque en stock.
// `sticky` exige `align-self:flex-start` (une colonne étirée n'a nulle part où
// coller) et un `top` explicite. Sur mobile elle repasse en flux normal : elle
// est alors SOUS la frise, où coller n'aurait aucun sens.
const CSS_PILOTAGE = `
.fbz-pilotage{display:grid;grid-template-columns:minmax(0,1fr) ${LARGEUR_COLONNE}px;gap:0;align-items:start}
.fbz-pilotage-aside{border-left:1px solid ${C.carteBord};background:${C.colonne};padding:18px 14px 30px;position:sticky;top:0;align-self:flex-start}
.fbz-pilotage-main{padding:18px 20px 60px;min-width:0}
@media (max-width: 860px){
  .fbz-pilotage{grid-template-columns:minmax(0,1fr)}
  .fbz-pilotage-aside{border-left:none;border-top:1px solid ${C.carteBord};position:static}
  .fbz-pilotage-main{padding:16px 14px 24px}
}
`

function isoDayOfWeek(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return dow === 0 ? 7 : dow
}

// Drain photo de l'orchestrateur double-casquette (un hook ne peut pas être
// conditionnel — d'où ce composant porteur).
function OrchestrateurPhotoDrain({ onUploaded }) {
  useMenagePhotoUploadQueue({ onUploaded })
  return null
}

// `onControler` (LOT FRISE CONTRÔLE) : prop OPTIONNELLE — le câblage App
// (navigation vers l'onglet Contrôle) se fait à l'intégration ; sans elle,
// aucun bouton « Contrôler » n'apparaît nulle part.
export default function MenageSuiviView({ basculePage = null, cfg, currentUser, onNavigate, stockCentral, destination, onAller, onDestinationConsommee, onControler, controlesATraiter = 0, onSansAgentJour = null }) {
  const etroitSpec = useEtroit() // valeurs pixel du bandeau (spec 2026-08-22 §2)
  // VRAIE date du jour — sert les DROITS (incident, checklist), jamais l'affichage seul.
  const today = useMemo(() => todayParisIso(), [])
  // Jour REGARDÉ — pilote l'affichage. Défaut : aujourd'hui.
  // ⚠️ IL SURVIT À L'ALLER-RETOUR D'ONGLET (correction Dimitri 2026-08-14 :
  // « je clique Traiter sur une demande du 17, je reviens, et je suis revenu à
  // aujourd'hui »). La vue est démontée au changement d'onglet → l'état vit en
  // sessionStorage, VALABLE LE JOUR MÊME SEULEMENT : le lendemain, la page
  // rouvre sur aujourd'hui (un jour regardé hier n'est plus une intention).
  const [jour, setJourBrut] = useState(() => {
    try {
      const m = JSON.parse(sessionStorage.getItem('fbz-suivi-jour') || 'null')
      if (m && m.pose === today && /^\d{4}-\d{2}-\d{2}$/.test(m.jour || '')) return m.jour
    } catch (_) { /* stockage indisponible → défaut */ }
    return today
  })
  const setJour = useCallback((j) => {
    setJourBrut(j)
    try { sessionStorage.setItem('fbz-suivi-jour', JSON.stringify({ jour: j, pose: today })) }
    catch (_) { /* stockage indisponible → l'état mémoire suffit */ }
  }, [today])

  // ⚠️ ON ARRIVE PEUT-ÊTRE DU MOIS. La destination porte le jour à ouvrir et le
  // chemin du RETOUR : un lien qui emmène sans ramener enferme dans la frise.
  // Consommée UNE FOIS puis effacée — sinon revenir sur l'onglet plus tard
  // rouvrirait le jour d'il y a une heure.
  const [retour, setRetour] = useState(null)
  useEffect(() => {
    const d = pourOnglet(destination, ONGLET_FRISE)
    if (!d) return
    if (d.jour) setJour(d.jour)
    setRetour(cheminRetour(d))
    onDestinationConsommee && onDestinationConsommee()
  }, [destination, onDestinationConsommee, setJour])

  const veille = useMemo(() => decalerJour(jour, -1), [jour])
  const lendemain = useMemo(() => decalerJour(jour, 1), [jour])

  const { weekStart, weekEnd } = useMemo(() => {
    const dow = isoDayOfWeek(jour)
    const ws = decalerJour(jour, -(dow - 1))
    return { weekStart: ws, weekEnd: decalerJour(ws, 6) }
  }, [jour])

  // Fenêtre de chargement : la semaine, ÉLARGIE à la veille et au lendemain du
  // jour regardé — un lundi, « hier » tombe dans la semaine précédente, et sans
  // cet élargissement le bilan de la veille serait vide sans raison visible.
  const bornes = useMemo(() => {
    const min = weekStart < veille ? weekStart : veille
    const max = weekEnd > lendemain ? weekEnd : lendemain
    return { min, max }
  }, [weekStart, weekEnd, veille, lendemain])

  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [nowHHMM, setNowHHMM] = useState(() => nowParisHHMM())
  useEffect(() => {
    const id = setInterval(() => setNowHHMM(nowParisHHMM()), 60000)
    return () => clearInterval(id)
  }, [])

  const [checklistModal, setChecklistModal] = useState(null)
  const [incidentModal, setIncidentModal] = useState(null)
  const [reassignMsg, setReassignMsg] = useState(null)
  const [guestTimes, setGuestTimes] = useState({})
  const [horaires, setHoraires] = useState({ pending: [], granted: [], hubActif: false })
  // Les règles d'assignation sont de la CONFIGURATION : elles ne s'ouvrent que
  // si on les demande, et pleine largeur (le sélecteur par logement ne tient
  // pas dans 235 px de colonne).
  const [reglesOuvertes, setReglesOuvertes] = useState(false)

  // FRISE — vue courante et interventions. L'ordre et les durées vivent
  // désormais EN BASE (migration 20261122000001) : plus aucun état local qui
  // ferait croire à un enregistrement qui n'existe pas.
  const [vueFrise, setVueFrise] = useState('logement')
  // ⚠️ LE FILTRE DU BANDEAU (maquette finale 2026-08-15) : un seul état, comme
  // sur le Mois — le clic ÉCLAIRE la frise (voile .2), jamais un panneau.
  // { type: 'departs' | 'arrivees' | 'danger' | 'pas-valide' | 'finis' }
  const [filtreJour, setFiltreJour] = useState(null)
  // Échap replie la liste dépliée du bandeau (et retire le voile — c'est le
  // même état). Pas de clic-dehors ici : le filtre sert à REGARDER la frise.
  useEchapPourFermer(() => setFiltreJour(null), !!filtreJour)
  // « Durées par logement » se déplie EN DESSOUS, pleine largeur — EXACTEMENT
  // comme les règles d'assignation (correction Dimitri 2026-08-15 : comprimé
  // dans la colonne, le contenu était illisible).
  const [dureesOuvertes, setDureesOuvertes] = useState(false)
  const [interventions, setInterventions] = useState([])
  const [modaleIntervention, setModaleIntervention] = useState(false)
  const [modaleDuree, setModaleDuree] = useState(null) // { bloc }
  const [recap, setRecap] = useState(null)             // { email }
  // APERÇU OPTIMISTE du seul glissement en cours : le bloc doit suivre le doigt
  // sans attendre l'aller-retour réseau. Il est effacé dès que la base a
  // répondu — et REMIS À L'ÉTAT ANTÉRIEUR si elle refuse.
  const [heuresOptimistes, setHeuresOptimistes] = useState({})

  const visibleEmails = useMemo(() => computeVisibleEmails(currentUser), [currentUser])
  // GARDE 9, RÉVISÉE (mesure du 2026-08-14). « Agents rencontrés dans la
  // fenêtre » avait une limite réelle : un agent SANS ménage ce jour-là était
  // INASSIGNABLE pour l'admin — le menu ne le proposait pas, et le clic à côté
  // ne disait rien. L'admin doit pouvoir assigner N'IMPORTE QUEL agent
  // (décision Dimitri). La liste des comptes vient de l'endpoint admin
  // existant ; on ne garde que menage + orchestrateur — JAMAIS proprietaire :
  // c'est précisément l'anomalie « proprio-agent » trouvée en prod, née d'un
  // pool qui contenait un proprio.
  // ── NON-ASSIGNÉS À VENIR (J..J+14) ─────────────────────────────────────────
  // 🔴 CE QUE PERSONNE NE VOYAIT S'ACCUMULER (mesure prod 2026-08-14 : 11 runs
  // lc3 sans agent jusqu'au 23/08, dont personne n'aurait jamais rien su). La
  // frise montre UN jour ; les non-assignés au-delà n'existaient nulle part.
  // Horizon 14 jours : en deçà, ces 11-là auraient échappé au compte le jour de
  // leur découverte ; au-delà, les résas bougent trop pour que le chiffre parle.
  // AUCUN filtre client : la RLS borne (`admin_all` / `orchestrateur_select_
  // non_assigne`, lue avant d'écrire ceci — chacun voit SES non-assignés).
  // ── NON-ASSIGNÉS À VENIR (J..J+14) ─────────────────────────────────────────
  // 🔴 CE QUE PERSONNE NE VOYAIT S'ACCUMULER (mesure prod 2026-08-14 : 11 runs
  // lc3 sans agent jusqu'au 23/08, dont personne n'aurait jamais rien su). La
  // frise montre UN jour ; les non-assignés au-delà n'existaient nulle part.
  // Horizon 14 jours : en deçà, ces 11-là auraient échappé au compte le jour de
  // leur découverte ; au-delà, les résas bougent trop pour que le chiffre parle.
  // AUCUN filtre client : la RLS borne (`admin_all` / `orchestrateur_select_
  // non_assigne`, lue avant d'écrire ceci — chacun voit SES non-assignés).
  const [aAssigner, setAAssigner] = useState([])
  const rechargerAAssigner = useCallback(async () => {
    // `currentUser` directement — `pilote` est déclaré plus bas (TDZ, même
    // piège que `isAdmin` au lot précédent : le build ESLint ne le voit pas,
    // c'est une erreur d'exécution).
    const role = currentUser?.role
    if (role !== 'admin' && role !== 'orchestrateur') { setAAssigner([]); return }
    const { data, error: e } = await supabase
      .from('menage_runs')
      .select('id, scheduled_date')
      .is('assigned_provider_email', null)
      .in('status', ['scheduled', 'in_progress'])
      .gte('scheduled_date', today)
      .lte('scheduled_date', decalerJour(today, 14))
    if (e) { console.error('[pilotage] a_assigner', e.message || e); return }
    setAAssigner(Array.isArray(data) ? data : [])
  }, [currentUser?.role, today])
  // L-1b (mesuré 2026-08-19) : l'appel du MONTAGE partait sur des runs
  // encore vides (écran en chargement) — pur gaspillage, le compte se
  // recalcule de toute façon à l'arrivée des runs (la dépendance voulue).
  const runsDejaCharges = useRef(false)
  useEffect(() => {
    if (!runsDejaCharges.current) { runsDejaCharges.current = runs.length > 0 || !loading; if (!runsDejaCharges.current) return }
    rechargerAAssigner()
  }, [rechargerAAssigner, runs, loading])

  // LE BADGE « PLANNING » (décision Dimitri 2026-08-22) — les ménages SANS
  // AGENT du jour AFFICHÉ. La vue est la seule à savoir quel jour est regardé :
  // elle remonte le compte, App le pose sur la barre. Une seule valeur, jamais
  // deux compteurs de la même chose.
  useEffect(() => {
    if (typeof onSansAgentJour !== 'function') return
    const n = runs.filter((r) => r?.scheduled_date === jour
      && !r?.assigned_provider_email
      && r?.status !== 'completed' && r?.status !== 'skipped').length
    onSansAgentJour(n)
  }, [runs, jour, onSansAgentJour])

  const [agentsComptes, setAgentsComptes] = useState([])
  useEffect(() => {
    // `currentUser` directement — `isAdmin` est déclaré plus bas (TDZ).
    if (currentUser?.role !== 'admin') { setAgentsComptes([]); return }
    let annule = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/admin-users', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (annule) return
        setAgentsComptes((json?.users || [])
          .filter((u) => u?.role === 'menage' || u?.role === 'orchestrateur')
          .map((u) => u.email).filter(Boolean))
      } catch (e) {
        // Sans la liste, on retombe sur « rencontrés » : dégradé, jamais cassé.
        console.error('[pilotage] comptes agents', e?.message || e)
      }
    })()
    return () => { annule = true }
  }, [currentUser?.role])

  const agentsConnus = useMemo(
    () => [...new Set([
      ...(runs || []).map((r) => r?.assigned_provider_email).filter(Boolean),
      ...agentsComptes,
    ])],
    [runs, agentsComptes]
  )
  // LOT PRÉNOMS (2026-08-19) : les prénoms saisis du pool — {} en attendant
  // (le filet de dérivation joue), puis les libellés se recalculent.
  const [prenomsPool, setPrenomsPool] = useState({})
  useEffect(() => {
    let annule = false
    const role = currentUser?.role
    if (role !== 'orchestrateur' && role !== 'admin') { setPrenomsPool({}); return }
    fetchPoolPrenoms(role === 'admin' && agentsConnus.length ? { emails: agentsConnus } : {})
      .then((p) => { if (!annule) setPrenomsPool(p || {}) })
    return () => { annule = true }
  }, [currentUser?.role, agentsConnus])

  const assignOptions = useMemo(
    () => computeAssignOptions(currentUser, { agentsConnus, prenoms: prenomsPool }),
    [currentUser, agentsConnus, prenomsPool]
  )

  const [regles, setRegles] = useState({})
  const rechargerRegles = useCallback(async () => {
    if (currentUser?.role !== 'orchestrateur') return
    const { regles: r } = await chargerRegles(supabase)
    setRegles(r)
  }, [currentUser])
  useEffect(() => { rechargerRegles() }, [rechargerRegles])

  const nomAgent = useCallback((email) => {
    if (!email) return null
    const opt = assignOptions.find(o => o.value === email)
    // C-1 + LOT PRÉNOMS : label (prénom saisi) sinon dérivation — jamais
    // l'email brut.
    return opt ? opt.label : libelleDepuisEmail(email)
  }, [assignOptions])

  const isOrchestrateur = currentUser?.role === 'orchestrateur'
  // ⚠️ DEUX DRAPEAUX, PAS UN (décision Dimitri 2026-08-12, chantier
  // PILOTAGE/CONTRÔLE). `pilote` = qui organise la journée — l'orchestrateur ET
  // l'admin. `isOrchestrateur` reste utilisé SEUL là où l'ouverture serait un
  // doublon ou une faute : les incidents du pool (l'admin a l'onglet Incidents
  // juste à côté, plus complet) et le drain photo (l'admin a déjà le sien dans
  // AdminChecklistEditor — deux drains montés ensemble, c'est le piège
  // ADR-207/208). Renommer les deux en un seul aurait effacé cette distinction.
  const isAdmin = currentUser?.role === 'admin'

  // « N INCIDENTS À JUGER » — le trou du cockpit (constat 2026-08-14) : la file
  // existait dans l'onglet Incidents mais AUCUN signal ici. Lecture seule,
  // best-effort ; orchestrateur = pool (endpoint cloisonné), admin = RLS.
  const [incidentsAJuger, setIncidentsAJuger] = useState([])
  useEffect(() => {
    let annule = false
    ;(async () => {
      try {
        const liste = isOrchestrateur
          ? await fetchOrchestratorPoolIncidents({ status: 'en_cours', limit: 100 })
          : isAdmin
            ? (await listIncidents({ limit: 200 })).filter(i => normalizeStatus(i.status) === STATUS_INCIDENTS.EN_COURS)
            : []
        if (!annule) setIncidentsAJuger(Array.isArray(liste) ? liste : [])
      } catch (e) {
        // Un signal de cockpit ne casse jamais la page.
        console.error('[pilotage] incidents à juger', e?.message || e)
      }
    })()
    return () => { annule = true }
  }, [isOrchestrateur, isAdmin])

  const pilote = isOrchestrateur || isAdmin

  // ⚠️ LA FRISE DE L'ADMIN EST EN LECTURE SEULE PAR DÉFAUT (décision Dimitri
  // 2026-08-12). Il l'ouvre d'abord pour REGARDER ; pouvoir déplacer un bloc
  // d'un simple glissement lui ferait défaire l'organisation de son
  // orchestrateur sans s'en apercevoir. L'orchestrateur, lui, garde son écran
  // modifiable en permanence — ce mode ne le concerne pas.
  const [modeModif, setModeModif] = useState(false)
  const modifiable = isOrchestrateur || (isAdmin && modeModif)
  // Le mode se referme dès qu'on change de jour ou qu'on quitte : un mode
  // d'édition qui survit à la navigation est celui qu'on oublie ouvert.
  useEffect(() => { setModeModif(false) }, [jour])
  const [inviteModif, setInviteModif] = useState(false)
  const self = currentUser?.email

  const findLog = useCallback((logementId) => {
    if (!cfg) return null
    for (const prop of cfg.props || []) {
      for (const log of prop.logs || []) {
        if (log.id === logementId) return log
        for (const ch of log.chambres || []) {
          if (ch.id === logementId) return ch
        }
      }
    }
    return null
  }, [cfg])

  // ── GUICHET CLOISONNÉ (inchangé) — la RLS de `public.storage` refuse
  // `fbz8-cfg` aux rôles terrain, d'où la lecture par `menage_log_info`.
  const [logInfos, setLogInfos] = useState({})
  useEffect(() => {
    const cles = [...new Set(runs.map(r => `${r.logement_id}:${r.scheduled_date}`))]
    if (!cles.length) { setLogInfos({}); return }
    let cancelled = false
    ;(async () => {
      // L-1 (2026-08-19) : UN appel GROUPÉ par date au lieu d'un par logement
      // (rafale N+1 mesurée : menage_log_info ×9 au chargement). Le groupé
      // PRIME le cache unitaire — menus, panneau et checklist restent des
      // hits immédiats.
      const parDate = new Map()
      for (const cle of cles) {
        const sep = cle.lastIndexOf(':')
        const lid = cle.slice(0, sep)
        const date = cle.slice(sep + 1)
        if (!parDate.has(date)) parDate.set(date, [])
        parDate.get(date).push(lid)
      }
      await Promise.all([...parDate.entries()].map(([date, lids]) =>
        fetchLogInfos(lids, date).catch((e) => {
          console.error('[suivi] menage_log_info groupé', date, e?.message || e)
          return {}
        })))
      if (cancelled) return
      const entrees = await Promise.all(cles.map(async (cle) => {
        const sep = cle.lastIndexOf(':')
        const lid = cle.slice(0, sep)
        const date = cle.slice(sep + 1)
        try {
          const info = await fetchLogInfo(lid, date)
          const adresse = [
            info?.adresse_complete,
            [info?.code_postal, info?.ville].filter(Boolean).join(' '),
          ].filter(s => s && String(s).trim().length > 0).join(' · ')
          return [cle, {
            checkout: info?.timing?.checkout_time || null,
            checkin: info?.timing?.checkin_time || null,
            nom: info?.nom || null,
            adresse: adresse || null,
            boiteCles: (info?.codes_acces?.boite_cles && String(info.codes_acces.boite_cles).trim()) || null,
            // PANNEAU DU MÉNAGE — TOUS les codes du guichet (héritage parent
            // serveur) : le panneau affiche chaque code RENSEIGNÉ sur sa ligne.
            codes: info?.codes_acces || null,
            // ⚠️ DURÉE PAR DÉFAUT DU LOGEMENT. Elle était LUE plus bas
            // (`infoFor(...)?.dureeMenage`) mais jamais POSÉE ici : la frise
            // dessinait donc tous les blocs à 90 min, quelle que soit la
            // configuration, et la modale annonçait un défaut faux. Un champ
            // qu'on lit sans jamais l'écrire ne lève aucune erreur — il ment.
            dureeMenage: info?.menage?.menage_duree_min ?? null,
          }]
        } catch (e) {
          console.error('[suivi] menage_log_info', lid, e?.message || e)
          return null
        }
      }))
      if (cancelled) return
      setLogInfos(Object.fromEntries(entrees.filter(Boolean)))
    })()
    return () => { cancelled = true }
  }, [runs])

  // ── LOT FRISE CONTRÔLE — les contrôles des runs affichés ─────────────────
  // UNE requête pour N runs (jamais une par bloc), déclenchée par le RÉSULTAT
  // des runs (règle guichet : jamais au chargement). BEST-EFFORT : un échec
  // rend {} — pas de pastilles, jamais un écran cassé. RLS : admin tout,
  // orchestrateur pool, proprio 0 ligne par construction.
  const [controlesParRun, setControlesParRun] = useState({})
  // LA FRISE SE MET À JOUR APRÈS UN CONTRÔLE (correction Dimitri 22/08) : le
  // dépôt (onglet Contrôle) émet fbz:controle-depose → on recharge la carte
  // des contrôles sans recharger la page (rond vert, ligne validée, teinte).
  const [versionControles, setVersionControles] = useState(0)
  useEffect(() => {
    const maj = () => setVersionControles((v) => v + 1)
    window.addEventListener('fbz:controle-depose', maj)
    return () => window.removeEventListener('fbz:controle-depose', maj)
  }, [])
  useEffect(() => {
    const ids = [...new Set(runs.map(r => r?.id).filter(Boolean))]
    if (!ids.length) { setControlesParRun({}); return }
    let annule = false
    ;(async () => {
      const par = await chargerControlesParRun(supabase, ids)
      if (!annule) setControlesParRun(par)
    })()
    return () => { annule = true }
  }, [runs, versionControles])
  // Le détail LECTURE SEULE ouvert par la pastille œil (Échap + clic dehors
  // dans le panneau lui-même).
  const [detailControle, setDetailControle] = useState(null)

  // ── PANNEAU DU MÉNAGE — le DÉFAUT de contrôleur par logement ─────────────
  // Batch au niveau vue (.in sur les logements des runs affichés), RLS directe
  // (admin + orchestrateur), BEST-EFFORT : un échec rend {} — le panneau
  // montre la seule désignation par-ménage, jamais un écran cassé.
  // « IL EN FAUT MAINTENANT » — les manques signalés par les agents (une
  // seule donnée, la même que l'écran de contrôle et l'onglet Stock).
  const [manques, setManques] = useState([])
  const [versionManques, setVersionManques] = useState(0)
  useEffect(() => {
    const maj = () => setVersionManques((v) => v + 1)
    window.addEventListener('fbz:stock-signalements', maj)
    return () => window.removeEventListener('fbz:stock-signalements', maj)
  }, [])
  useEffect(() => {
    const role = currentUser?.role
    if (role !== 'admin' && role !== 'orchestrateur') { setManques([]); return }
    let annule = false
    fetchManques().then((liste) => { if (!annule) setManques(liste) })
    return () => { annule = true }
  }, [currentUser?.role, versionManques])

  const [controleurDefauts, setControleurDefauts] = useState({})
  const [controleursOuverts, setControleursOuverts] = useState(false)
  // SYNCHRO (Dimitri 2026-08-21) : les trois chemins de désignation écrivent
  // la même chose — l'événement re-déclenche cette lecture, le panneau de la
  // frise ET la ligne « Qui contrôle quoi par défaut » se rafraîchissent.
  const [versionDefauts, setVersionDefauts] = useState(0)
  useEffect(() => {
    const maj = () => setVersionDefauts((v) => v + 1)
    window.addEventListener('fbz:controle-designation', maj)
    return () => window.removeEventListener('fbz:controle-designation', maj)
  }, [])
  useEffect(() => {
    const role = currentUser?.role
    if (role !== 'admin' && role !== 'orchestrateur') { setControleurDefauts({}); return }
    const ids = [...new Set([
      ...runs.map(r => r?.logement_id).filter(Boolean),
      ...(Array.isArray(currentUser?.logIds) ? currentUser.logIds : []),
    ])]
    if (!ids.length) { setControleurDefauts({}); return }
    let annule = false
    ;(async () => {
      const par = await chargerControleurDefauts(supabase, ids)
      if (!annule) setControleurDefauts(par)
    })()
    return () => { annule = true }
  }, [currentUser?.role, currentUser?.logIds, runs, versionDefauts])

  // ── PANNEAU DU MÉNAGE — les incidents du bloc 5, côté ADMIN ──────────────
  // L'orchestrateur a déjà son batch pool (GARDE 4 plus bas) ; l'admin lit la
  // table `incidents` en RLS directe (admin_all), MÊMES colonnes que la
  // projection pool. BEST-EFFORT : échec → null → bloc ABSENT du panneau.
  const [adminIncidents, setAdminIncidents] = useState([])
  useEffect(() => {
    if (currentUser?.role !== 'admin') { setAdminIncidents([]); return }
    const ids = [...new Set(runs.map(r => r?.logement_id).filter(Boolean))]
    if (!ids.length) { setAdminIncidents([]); return }
    let annule = false
    ;(async () => {
      try {
        const { data, error: e } = await supabase
          .from('incidents')
          .select('id, status, logement_id, reported_by, created_at, run_id, label')
          .in('logement_id', ids)
          .order('created_at', { ascending: false })
          .limit(300)
        if (annule) return
        if (e) {
          console.error('[panneau] incidents admin', e.message || e)
          setAdminIncidents(null)
          return
        }
        setAdminIncidents(Array.isArray(data) ? data : [])
      } catch (e) {
        console.error('[panneau] incidents admin', e?.message || e)
        if (!annule) setAdminIncidents(null)
      }
    })()
    return () => { annule = true }
  }, [currentUser?.role, runs])

  const infoFor = useCallback(
    (logementId, dateIso) => logInfos[`${logementId}:${dateIso}`] || null,
    [logInfos]
  )

  const checkoutTimeByLog = useCallback((logementId) => {
    const t = infoFor(logementId, jour)
    if (t?.checkout) return t.checkout
    const log = findLog(logementId)
    return log ? resolveLogTime(log, null, 'checkout') : null
  }, [infoFor, jour, findLog])
  const checkinTimeByLog = useCallback((logementId) => {
    const t = infoFor(logementId, jour)
    if (t?.checkin) return t.checkin
    const log = findLog(logementId)
    return log ? resolveLogTime(log, null, 'checkin') : null
  }, [infoFor, jour, findLog])

  const nomByLog = useCallback((logementId, dateIso) => {
    const t = infoFor(logementId, dateIso)
    if (t?.nom) return t.nom
    const log = findLog(logementId)
    return log?.nom || log?.id || logementId
  }, [infoFor, findLog])

  const sesLogements = useMemo(() => {
    const ids = Array.isArray(currentUser?.logIds) ? currentUser.logIds : []
    return ids.map(id => ({ id, nom: nomByLog(id, jour) }))
  }, [currentUser, nomByLog, jour])

  // ── ADR-302 — options du rédacteur de consignes (AFFICHAGE SEUL) ──────────
  // Logements = ses `logIds` (app_metadata signé), libellés via cfg.
  // Agents = son pool + « Moi ». Le cloisonnement RÉEL reste serveur
  // (readFreshOrchestratorScope) : ces listes ne CONFÈRENT aucun droit.
  const consigneLogementOptions = useMemo(() => {
    const ids = Array.isArray(currentUser?.logIds) ? currentUser.logIds : []
    return ids.map((id) => ({ id, nom: findLog(id)?.nom || id }))
  }, [currentUser, findLog])
  const consigneAgentOptions = useMemo(() => {
    const pool = Array.isArray(currentUser?.managed_agents) ? currentUser.managed_agents : []
    const self = currentUser?.email
    const out = pool.map((email) => ({ email, label: firstNameFromEmail(email) || email }))
    if (self) out.unshift({ email: self, label: 'Moi' })
    return out
  }, [currentUser])

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      // ⚠️ FAIL-CLOSED CONSERVÉ, MAIS PAS POUR QUI VOIT TOUT. Sans la seconde
      // condition, l'admin — dont la liste visible est vide PARCE QU'il n'a pas
      // de périmètre — voyait sa requête coupée ici et l'écran affichait
      // « 0 ménage », le filtre aval ayant pourtant bien été levé.
      if (!visibleEmails.length && !voitToutLeParc(currentUser)) { setRuns([]); return }
      // `heure_fixee` et `duree_min` sont LUES ici : sans elles, l'ordre posé
      // à la souris ne survivrait pas au rechargement — il serait écrit en base
      // et jamais relu, ce qui est pire que pas de persistance du tout.
      // ⚠️ `modifie_par` / `modifie_le` DOIVENT être demandées : PostgREST ne rend
      // que les colonnes du select. Livré sans elles le 2026-08-13 — la trace
      // « dernière main » ne s'affichait NULLE PART en prod, et la preuve
      // Playwright ne l'a pas vu parce que son mock ignorait le `select=`.
      // Depuis, le harnais de communication STRIP les colonnes non demandées.
      // + depart_reporte_au (chantier dates 2026-08-14) : le signal « départ
      // déplacé » posé par la cascade → motif « À régler » (motifDeLigne 2bis).
      // ⚠️ SÉQUENCEMENT : la migration 20261201000001 doit être APPLIQUÉE avant
      // le deploy de cette colonne dans le select (sinon 400 PostgREST).
      // + controleur_designe_email (panneau du ménage 2026-08-19) : la
      // désignation PAR-MÉNAGE, résolue avant le défaut du logement.
      const cols = 'id, logement_id, scheduled_date, status, assigned_provider_email, completed_at, guest_count, items_checked, photos, heure_fixee, duree_min, modifie_par, modifie_le, depart_reporte_au, controleur_designe_email'
      const { data, error: e } = await buildRunsProviderFilter(
        supabase.from('menage_runs').select(cols),
        currentUser,
        visibleEmails
      )
        .gte('scheduled_date', bornes.min)
        .lte('scheduled_date', bornes.max)
      if (e) throw e
      setRuns(data || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [currentUser, visibleEmails, bornes])

  useEffect(() => { reload() }, [reload])

  // DEMANDES D'HORAIRES en attente — alimente la carte de droite ET le bandeau
  // porté par la carte du ménage que la demande décale.
  useEffect(() => {
    // GARDE 1 — OUVERTE À L'ADMIN. Vérifié dans le CODE du handler (pas dans
    // son commentaire) : `services_orchestrateur_hourly_list` fait
    // `isAdmin → logIds = null`, donc aucun filtre. Zéro changement serveur.
    if (!pilote) { setHoraires({ pending: [], granted: [], hubActif: false }); return }
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'services_orchestrateur_hourly_list' }),
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (cancelled) return
        setHoraires({
          pending: Array.isArray(json?.pending) ? json.pending : [],
          // CAS 5 (règle des repères, 2026-08-16) : ce qui a été PAYÉ prime
          // sur une annonce — il faut savoir qu'un accordé existe.
          granted: Array.isArray(json?.granted_context) ? json.granted_context : [],
          hubActif: json?.hub_actif === true,
        })
      } catch (e) {
        // Jamais bloquant : sans demandes, la carte se tait, l'écran vit.
        console.error('[suivi] horaires', e?.message || e)
      }
    })()
    return () => { cancelled = true }
  }, [pilote, currentUser?.email])

  // ALERTES : calculées pour le SEUL jour d'aujourd'hui (cf. en-tête de fichier).
  const jourEstAujourdhui = jour === today
  // OÙ SE SITUE LE JOUR REGARDÉ — l'heure courante ne veut rien dire ailleurs
  // qu'aujourd'hui. La frise s'en sert pour le remplissage des blocs, les
  // arrivées déjà faites et le classement en groupes : trois calculs qui, sans
  // cette information, peindraient demain comme s'il était à moitié écoulé.
  const positionJour = jour === today ? 'aujourdhui' : (jour < today ? 'passe' : 'futur')
  const { weekByAgent, todayRuns, alerts } = useMemo(
    () => aggregateSuivi(runs, {
      todayIso: jour,
      weekStart, weekEnd,
      nowHHMM: jourEstAujourdhui ? nowHHMM : null,
      checkoutTimeByLog, checkinTimeByLog,
    }),
    [runs, jour, weekStart, weekEnd, nowHHMM, jourEstAujourdhui, checkoutTimeByLog, checkinTimeByLog]
  )

  // ── L'ACCUSÉ DE RÉCEPTION (vu / pas vu) ───────────────────────────────────
  // L'agent est prévenu (bandeaux) et acquitte (vu_le) — mais l'orchestrateur
  // ne savait pas si le message était VU. Lecture pool (policy
  // evenements_select_pool) ; l'acquittement reste à l'agent. Affiché dans le
  // DÉTAIL au clic seulement (décision Dimitri : « ça suffit »).
  const [evenementsParRun, setEvenementsParRun] = useState({})
  useEffect(() => {
    const role = currentUser?.role
    if (role !== 'admin' && role !== 'orchestrateur') { setEvenementsParRun({}); return }
    const ids = (todayRuns || []).map((r) => r.id).filter(Boolean)
    if (!ids.length) { setEvenementsParRun({}); return }
    let annule = false
    ;(async () => {
      const { data, error } = await supabase
        .from('menage_evenements_agent')
        .select('run_id, type, destinataire_email, vu_le, cree_le')
        .in('run_id', ids)
        .order('cree_le', { ascending: false })
        .limit(100)
      if (error) { console.error('[pilotage] accuses', error.message || error); return }
      if (annule) return
      const par = {}
      for (const e of data || []) if (e.run_id && !par[e.run_id]) par[e.run_id] = e
      setEvenementsParRun(par)
    })()
    return () => { annule = true }
  }, [currentUser?.role, todayRuns])


  useEffect(() => { setGuestTimes({}) }, [currentUser?.email])

  useEffect(() => {
    // GARDE 2 — VOLONTAIREMENT FERMÉE À L'ADMIN, et ce n'est pas une privation.
    // `orchestrateur_guest_times` REFUSE l'admin par construction (fermeture
    // IDOR ADR-229/230) ; l'ouvrir donnerait un 403 à chaque chargement. Inutile
    // de toute façon : le guichet `menage_log_info` rend déjà les heures RÉELLES
    // à l'admin (ADR-226). On ouvre la fonction, pas l'endpoint.
    if (!isOrchestrateur) { setGuestTimes({}); return }
    const runIds = todayRuns.map(r => r.id).filter(Boolean)
    if (runIds.length === 0) { setGuestTimes({}); return }
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'orchestrateur_guest_times', run_id: runIds }),
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (!cancelled && json && typeof json === 'object') setGuestTimes(json)
      } catch (_) { /* silencieux : pas de réel → ligne masquée */ }
    })()
    return () => { cancelled = true }
  }, [isOrchestrateur, todayRuns, currentUser?.email])

  // ═══ CONTACT VOYAGEUR AU CLIC (GO Dimitri 2026-08-17) ═══
  // Rien n'arrive à l'écran avant le clic ; le clic demande CETTE carte au
  // serveur (tracé fail-closed côté serveur) ; la fermeture efface tout —
  // aucun cache, aucun stockage.
  const [contactBulle, setContactBulle] = useState(null)
  // La bulle porte AUSSI ce que la carte a dégradé (Dimitri 2026-08-17) :
  // heure + nombre voyagent avec le clic (déjà connus de la frise) et
  // s'affichent quel que soit l'état du chargement.
  const onContactVoyageur = useCallback(async ({ runId, cote, ancre, heure, personnes }) => {
    const socle = { runId, cote, ancre, heure: heure || null, personnes: personnes || null }
    setContactBulle({ ...socle, chargement: true })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) { setContactBulle(null); return }
      const res = await fetch('/api/beds24-mapping', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'orchestrateur_guest_contact', run_id: runId, cote }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setContactBulle({ ...socle, erreur: json?.error || 'Consultation impossible' })
        return
      }
      setContactBulle({ ...socle, nom: json?.nom || null, telephone: json?.telephone || null })
    } catch (_) {
      setContactBulle({ ...socle, erreur: 'Consultation impossible' })
    }
  }, [])
  const fermerContact = useCallback(() => setContactBulle(null), [])
  // FERMETURE STANDARD (tri Dimitri 2026-08-17) : la bulle de contact se
  // ferme à Échap et au clic dehors, comme le menu d'assignation.
  useEchapPourFermer(fermerContact, !!contactBulle)
  const refBulleContact = useRef(null)
  useEffect(() => {
    if (!contactBulle) return undefined
    const surPointerDown = (e) => {
      if (refBulleContact.current && !refBulleContact.current.contains(e.target)) fermerContact()
    }
    const timer = setTimeout(() => document.addEventListener('pointerdown', surPointerDown, true), 0)
    return () => { clearTimeout(timer); document.removeEventListener('pointerdown', surPointerDown, true) }
  }, [contactBulle, fermerContact])

  const handleReassign = useCallback(async (run, newEmail) => {
    setReassignMsg(null)
    if (!newEmail || newEmail === run.assigned_provider_email) return
    try {
      const { data, error: e } = await supabase
        .from('menage_runs')
        .update({ assigned_provider_email: newEmail })
        .eq('id', run.id)
        .select('id')
      if (e || !data || data.length === 0) {
        setReassignMsg('❌ Réassignation refusée (hors pool / hors périmètre / run verrouillé ou terminé).')
        return
      }
      setReassignMsg('✅ Ménage réassigné.')
      await reload()
    } catch (e) {
      console.error('[reassign] échec', e?.message || e)
      setReassignMsg('❌ Réassignation refusée (hors pool / hors périmètre / run verrouillé ou terminé).')
    }
  }, [reload])

  // ── PANNEAU DU MÉNAGE — RETIRER l'assignation (bouton carré terracotta).
  // MÊME chemin serveur que le menu (UPDATE menage_runs mono-colonne), assigné
  // à NULL. Rowcount-check : un refus RLS ne lève pas d'erreur, il rend zéro
  // ligne — l'orchestrateur, dont le WITH CHECK exige cible ∈ pool OU soi, se
  // verra refuser le retrait ; on le DIT au lieu de laisser l'écran mentir.
  const handleRetirerAssignation = useCallback(async (run) => {
    setReassignMsg(null)
    if (!run?.assigned_provider_email) return
    try {
      const { data, error: e } = await supabase
        .from('menage_runs')
        .update({ assigned_provider_email: null })
        .eq('id', run.id)
        .select('id')
      if (e || !data || data.length === 0) {
        setReassignMsg('❌ Retrait refusé (hors périmètre / run verrouillé ou terminé).')
        return
      }
      setReassignMsg('✅ Assignation retirée.')
      await reload()
    } catch (e) {
      console.error('[panneau] retirer assignation', e?.message || e)
      setReassignMsg('❌ Retrait refusé (hors périmètre / run verrouillé ou terminé).')
    }
  }, [reload])

  // ── PANNEAU DU MÉNAGE — désignation PAR-MÉNAGE du contrôleur ─────────────
  // OPTIMISTE à l'affichage (le bouton s'allume sous le doigt), ROLLBACK si le
  // serveur refuse (controle_designer, clampé parc côté serveur).
  const handleDesignerControleur = useCallback(async (run, email) => {
    setReassignMsg(null)
    const avant = run.controleur_designe_email ?? null
    setRuns(prev => prev.map(r => (r.id === run.id ? { ...r, controleur_designe_email: email } : r)))
    const { ok, erreur } = await designerControleurRun({ runId: run.id, controleurEmail: email })
    if (!ok) {
      setRuns(prev => prev.map(r => (r.id === run.id ? { ...r, controleur_designe_email: avant } : r)))
      setReassignMsg(`❌ Désignation refusée. ${erreur || ''}`.trim())
    }
  }, [])

  const handleRunCompleted = useCallback(async () => {
    setChecklistModal(null)
    await reload()
  }, [reload])

  const onPhotoUploaded = useCallback(async (item) => {
    const next = await commitUploadedPhoto(supabase, item)
    setRuns(prev => prev.map(r => (r.id === item.runId ? { ...r, photos: next } : r)))
  }, [])

  // ── DÉRIVÉS D'AFFICHAGE ────────────────────────────────────────────────────
  const alertIds = useMemo(() => new Set(alerts.map(r => r.id)), [alerts])
  const jourEstPasse = jour < today

  // « En retard » réunit deux faits de même nature : aujourd'hui, un check-in
  // franchi sans validation ; sur un jour passé, un ménage jamais validé.
  const estEnRetard = useCallback(
    (run) => alertIds.has(run.id) || (jourEstPasse && run?.status !== 'completed'),
    [alertIds, jourEstPasse]
  )
  const estOrphelin = useCallback((run) => !run?.assigned_provider_email, [])


  const sortedJour = useMemo(() => {
    const arr = [...todayRuns]
    arr.sort((a, b) => {
      const ao = estOrphelin(a) ? 0 : 1
      const bo = estOrphelin(b) ? 0 : 1
      if (ao !== bo) return ao - bo
      const aa = estEnRetard(a) ? 0 : 1
      const bb = estEnRetard(b) ? 0 : 1
      if (aa !== bb) return aa - bb
      const ta = checkoutTimeByLog(a.logement_id) || '99:99'
      const tb = checkoutTimeByLog(b.logement_id) || '99:99'
      return ta < tb ? -1 : ta > tb ? 1 : 0
    })
    return arr
  }, [todayRuns, estOrphelin, estEnRetard, checkoutTimeByLog])

  // Changer de jour efface l'aperçu optimiste : il ne vaut que pour le
  // glissement en cours.
  useEffect(() => { setHeuresOptimistes({}) }, [jour])

  // INTERVENTIONS — même fenêtre que les ménages. La RLS borne le périmètre.
  const rechargerInterventions = useCallback(async () => {
    // GARDE 3 — OUVERTE. `interventions_admin_all` couvre déjà l'admin en base.
    if (!pilote) { setInterventions([]); return }
    const { interventions: list } = await chargerInterventions(supabase, { du: bornes.min, au: bornes.max })
    setInterventions(list)
  }, [pilote, bornes])
  useEffect(() => { rechargerInterventions() }, [rechargerInterventions])

  const bilan = useMemo(() => bilanVeille(runs, veille, today), [runs, veille, today])
  const demain = useMemo(() => apercuLendemain(runs, lendemain), [runs, lendemain])

  const idxHoraires = useMemo(() => indexerDemandesHoraires(horaires.pending), [horaires.pending])

  // AVIS — remontés ici parce que DEUX consommateurs en ont besoin : la carte
  // Propreté et le récap d'un agent. Les charger deux fois ferait deux requêtes
  // pour la même donnée, et deux moments où elles pourraient diverger.
  const [avisCartes, setAvisCartes] = useState([])
  const [avisStats, setAvisStats] = useState({ count: 0, avg: null })
  useEffect(() => {
    const role = currentUser?.role
    if (role !== 'orchestrateur' && role !== 'menage' && role !== 'admin') return
    let cancelled = false
    ;(async () => {
      try {
        const payload = await fetchMenageAvis()
        if (cancelled) return
        setAvisCartes(buildAvisIndicatorCards(payload))
        setAvisStats(payload?.statsOverall || { count: 0, avg: null })
      } catch (e) {
        console.error('[suivi] avis', e?.message || e)
      }
    })()
    return () => { cancelled = true }
  }, [currentUser?.role, currentUser?.email])

  // Incidents du pool — sert le compteur « incidents signalés » du récap agent.
  // On réutilise le lecteur existant plutôt que d'en écrire un second.
  const [incidents, setIncidents] = useState([])
  useEffect(() => {
    // GARDE 4 — RESTE FERMÉE. `orchestrateur_incidents_pool` refuserait l'admin,
    // et l'ouvrir ferait un DOUBLON APPAUVRI : le sous-onglet Incidents, juste à
    // côté, lui montre tout — argent et note admin compris.
    if (!isOrchestrateur) { setIncidents([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const list = await fetchOrchestratorPoolIncidents({ limit: 200 })
        if (!cancelled) setIncidents(Array.isArray(list) ? list : (list?.incidents || []))
      } catch (e) {
        console.error('[suivi] incidents', e?.message || e)
      }
    })()
    return () => { cancelled = true }
  }, [isOrchestrateur])

  // STOCK — produits sous seuil, calculés avec le MÊME helper que l'onglet
  // Stock (aucune règle de seuil dupliquée ici).
  const stockBas = useMemo(() => {
    const central = stockCentral && typeof stockCentral === 'object' ? stockCentral : {}
    const produits = Array.isArray(cfg?.consommables) ? cfg.consommables : []
    return produitsSousSeuil(produits.map(p => ({
      id: p?.id,
      nom: p?.nom,
      qtyDispo: central[p?.id]?.qtyDispo || 0,
      seuilMin: central[p?.id]?.seuilMin || 0,
    })))
  }, [cfg, stockCentral])

  const orphanWeekRow = useMemo(
    () => weekByAgent.find(a => a.email === '(non assigné)') || null,
    [weekByAgent]
  )
  const agentWeekRows = useMemo(
    () => weekByAgent.filter(a => a.email !== '(non assigné)'),
    [weekByAgent]
  )

  // ── Ce que la frise a besoin de savoir ────────────────────────────────────
  // Un logement porte son nom ET sa durée de ménage par défaut. La durée vient
  // du guichet (`menage.menage_duree_min`, whitelist serveur) ; une valeur
  // posée dans la session la surcharge le temps de la revue.
  const logementsById = useMemo(() => {
    const out = {}
    for (const run of todayRuns) {
      const id = run.logement_id
      if (out[id]) continue
      out[id] = {
        id,
        nom: nomByLog(id, run.scheduled_date),
        menage_duree_min: infoFor(id, run.scheduled_date)?.dureeMenage ?? null,
      }
    }
    for (const it of interventions) {
      const id = it.logement_id
      if (id && !out[id]) out[id] = { id, nom: nomByLog(id, jour), menage_duree_min: null }
    }
    return out
  }, [todayRuns, interventions, nomByLog, infoFor, jour])

  // ⚠️ CAS 5 : « Marc a payé pour arriver à 15h, puis annonce 19h — on garde
  // 15h. On ne dessine pas ce qui ne fait pas foi. » Une annonce du même côté
  // qu'un accordé (payé) est retirée des guestTimes AVANT tout consommateur :
  // cartes, bulles vertes, créneau du glisser, bandeau.
  const accordesParCle = useMemo(() => {
    const out = {}
    for (const g of horaires.granted || []) {
      const cle = `${g.logement_id}|${g.event_date}`
      out[cle] = out[cle] || { early: false, late: false }
      if (g.event_kind === 'early_checkin') out[cle].early = true
      if (g.event_kind === 'late_checkout') out[cle].late = true
    }
    return out
  }, [horaires.granted])
  const guestTimesEffectifs = useMemo(() => {
    const out = {}
    for (const r of runs) {
      const gt = guestTimes[r.id]
      if (!gt) continue
      const acc = accordesParCle[`${r.logement_id}|${r.scheduled_date}`]
      out[r.id] = {
        arrival: acc?.early ? null : gt.arrival,
        departure: acc?.late ? null : gt.departure,
        // Le NOMBRE de l'arrivant traverse tel quel : un accordé change une
        // HEURE, jamais le nombre de voyageurs (lot arrivant 2026-08-17).
        arrivant_count: gt.arrivant_count ?? null,
      }
    }
    return out
  }, [runs, guestTimes, accordesParCle])

  const horairesParLogement = useMemo(() => {
    const out = {}
    for (const id of Object.keys(logementsById)) {
      out[id] = { checkout: checkoutTimeByLog(id), checkin: checkinTimeByLog(id) }
    }
    return out
  }, [logementsById, checkoutTimeByLog, checkinTimeByLog])

  // Les exceptions de durée sont portées par `menage_runs.duree_min`, lue plus
  // haut : aucune transformation à faire ici.
  const runsFrise = sortedJour

  const nowMin = useMemo(() => minutesDeHeure(nowHHMM), [nowHHMM])

  // L'ordre vient de la BASE ; l'aperçu optimiste ne le recouvre que le temps
  // de l'aller-retour.
  const heuresFixees = useMemo(() => {
    const out = {}
    for (const r of todayRuns) {
      const h = heureCourte(r.heure_fixee)
      if (h) out[r.id] = h
    }
    for (const it of interventions) {
      if (it.heure_fixee) out[it.id] = it.heure_fixee
    }
    return { ...out, ...heuresOptimistes }
  }, [todayRuns, interventions, heuresOptimistes])

  // ── LES GESTES → ÉCRITURES ────────────────────────────────────────────────
  // Optimiste à l'AFFICHAGE, jamais optimiste sur le RÉSULTAT : un refus RLS ne
  // lève pas d'erreur, il renvoie zéro ligne. On remet donc le bloc où il était
  // et on le dit.
  const [messageFrise, setMessageFrise] = useState(null)

  const onGeste = useCallback(async (bloc, { debut, fin, aBute, mode }) => {
    const heure = formaterHeure(debut)
    const duree = fin - debut
    setReassignMsg(null)
    setHeuresOptimistes(prev => ({ ...prev, [bloc.id]: heure }))

    // Le bloc a buté sur la plage : on ne colore pas en rouge une limite
    // infranchissable — on explique pourquoi elle est là.
    setMessageFrise(aBute
      ? 'Le bloc bute sur la plage — le ménage doit tenir entre le départ et l\'arrivée.'
      : null)

    const estInter = bloc.type === 'intervention'
    let ok = true
    let erreur = null

    if (estInter) {
      const r = await majIntervention(supabase, bloc.id, { heure_fixee: heure, duree_min: duree })
      ok = r.ok; erreur = r.erreur
    } else {
      const rH = await fixerHeureRun(supabase, bloc.id, heure)
      ok = rH.ok; erreur = rH.erreur
      // ÉTIRER change la durée DE CE MÉNAGE-LÀ. La règle du logement ne bouge
      // pas — et l'écran le dit, sinon on croirait avoir reparamétré le logement.
      if (ok && mode !== 'deplacer' && duree !== bloc.dureeMin) {
        const rD = await fixerDureeRun(supabase, bloc.id, duree)
        ok = rD.ok; erreur = rD.erreur
        if (ok) {
          setMessageFrise(`Durée portée à ${formaterDuree(duree)} pour ce ménage seulement — le défaut du logement (${formaterDuree(bloc.dureeParDefaut)}) ne bouge pas.`)
        }
      }
    }

    if (!ok) {
      setHeuresOptimistes(prev => { const n = { ...prev }; delete n[bloc.id]; return n })
      setReassignMsg(`❌ ${erreur}`)
      return
    }
    if (estInter) await rechargerInterventions()
    else await reload()
    setHeuresOptimistes(prev => { const n = { ...prev }; delete n[bloc.id]; return n })
  }, [reload, rechargerInterventions])

  // TRACER : sur une ligne « heure à fixer », le geste pose l'heure ET la durée.
  const onTracer = useCallback(async (bloc, { debut, fin }) => {
    await onGeste(bloc, { debut, fin, aBute: false, mode: 'tracer' })
  }, [onGeste])

  // CHANGER D'AGENT depuis le menu du bloc.
  const onChoisirAgent = useCallback(async (bloc, email) => {
    if (bloc.type === 'intervention') {
      setReassignMsg(null)
      const { ok, erreur } = await majIntervention(supabase, bloc.id, { assigned_provider_email: email })
      if (!ok) { setReassignMsg(`❌ ${erreur}`); return }
      await rechargerInterventions()
      return
    }
    if (email === bloc.agent) return
    await handleReassign(bloc.run, email)
  }, [handleReassign, rechargerInterventions])

  // ⚠️ NOTRE GRAMMAIRE (Dimitri 2026-08-22) : la suppression se confirme dans
  // une feuille (téléphone) / une carte centrée (ordinateur), jamais dans une
  // boîte du navigateur. La phrase dit CE QUI VA SE PASSER.
  const [blocASupprimer, setBlocASupprimer] = useState(null)
  const onSupprimerIntervention = useCallback((bloc) => { setBlocASupprimer(bloc) }, [])
  const confirmerSuppression = useCallback(async () => {
    const bloc = blocASupprimer
    setBlocASupprimer(null)
    if (!bloc) return
    const { ok, erreur } = await supprimerIntervention(supabase, bloc.id)
    if (!ok) { setReassignMsg(`❌ ${erreur}`); return }
    await rechargerInterventions()
  }, [blocASupprimer, rechargerInterventions])

  // ⚠️ LES GESTES SONT BRIDÉS À LA SOURCE, pas dans l'affichage. Masquer une
  // poignée sans fermer le geste laisserait passer un glissement au clavier ou
  // un rendu qu'on aurait oublié. Ici, en lecture seule, AUCUNE écriture ne peut
  // partir — et le geste ne se contente pas d'échouer : il PROPOSE d'activer.
  const onAcquitter = useCallback(async (bloc) => {
    if (!Number.isFinite(bloc?.debut) || !bloc?.run?.id) return
    const { ok, erreur } = await accepterRecalage(supabase, bloc.run.id, bloc.debut)
    if (!ok) { setReassignMsg(`❌ ${erreur}`); return }
    await reload()
  }, [reload])

  const gestesFrise = useMemo(() => {
    if (modifiable) return { onGeste, onTracer, onChoisirAgent, onAcquitter }
    const refuser = () => { setInviteModif(true) }
    return { onGeste: refuser, onTracer: refuser, onChoisirAgent: refuser, onAcquitter: refuser }
  }, [modifiable, onGeste, onTracer, onChoisirAgent, onAcquitter])


  // Blocs de la SEMAINE (et non du seul jour) : un récap hebdomadaire calculé
  // sur une journée ne serait pas un récap.
  const blocsSemaine = useMemo(() => construireBlocs({
    runs: runs.filter(r => r.scheduled_date >= weekStart && r.scheduled_date <= weekEnd),
    interventions: interventions.filter(i => i.scheduled_date >= weekStart && i.scheduled_date <= weekEnd),
    logementsById, horaires: horairesParLogement, heuresFixees, guestTimes: guestTimesEffectifs,
  }), [runs, interventions, weekStart, weekEnd, logementsById, horairesParLogement, heuresFixees, guestTimesEffectifs])

  const blocsDuJour = useMemo(() => construireBlocs({
    runs: runsFrise, interventions, logementsById, horaires: horairesParLogement, heuresFixees, guestTimes: guestTimesEffectifs,
  }), [runsFrise, interventions, logementsById, horairesParLogement, heuresFixees, guestTimesEffectifs])

  const libelleSemaine = `Semaine du ${jourLabelCourt(weekStart)} au ${jourLabelCourt(weekEnd)}`

  // CONFLITS DU JOUR — calculés UNE FOIS ici parce que DEUX consommateurs en
  // ont besoin : le compteur du bandeau et le décompte de chaque agent dans la
  // colonne de droite. Deux calculs séparés finiraient par diverger, et un
  // écran qui compte deux fois la même chose de deux façons ne se croit plus.
  const conflitsDuJour = useMemo(() => detecterConflitsParAgent(blocsDuJour), [blocsDuJour])

  // « EN RÉSUMÉ » (téléphone) — les phrases, lues des blocs de LA frise.
  const resumeDuJour = useMemo(() => resumeJournee({
    blocs: blocsDuJour,
    conflitsParAgent: conflitsDuJour.parAgent,
    agentsPool: assignOptions.map((o) => o.value),
    self,
    nomAgent: (e) => prenomsPool[(e || '').toLowerCase()] || libelleDepuisEmail(e),
    nowMin,
    estAujourdhui: jourEstAujourdhui,
  }), [blocsDuJour, conflitsDuJour, assignOptions, self, prenomsPool, nowMin, jourEstAujourdhui])

  // ── LE CROISEMENT MÉNAGES × ARRIVÉES (bandeau v3, maquette 2026-08-15) ──
  // Ce que ni la frise ni la colonne ne disent : un ménage qui DÉBORDE sur une
  // arrivée. Arrivée = heure RÉELLE du formulaire (orchestrateur via endpoint
  // dédié ; admin : le guichet rend déjà le réel, ADR-226), sinon l'heure
  // d'arrivée par défaut du logement. ⚠️ AUCUN started_at n'existe en base :
  // le début « réel » d'un ménage démarré est approximé par son créneau posé.
  // Fin prévue : démarré → début posé + durée ; pas démarré → max(maintenant,
  // début posé) + durée (le max attrape AUSSI le créneau planifié trop tard —
  // sur-ensemble volontaire de la règle Dimitri, signalé 2026-08-15).
  const croisementJour = useMemo(() => {
    const departs = []
    const arrivees = []
    const dangers = []
    const pasValides = []
    const listes = { departs: [], arrivees: [], finis: [] }
    const ids = { departs: new Set(), arrivees: new Set(), danger: new Set(), pasValide: new Set(), finis: new Set() }
    const marqueurs = {}
    let finis = 0
    let totalMenages = 0
    for (const b of blocsDuJour) {
      const run = b.run
      if (!run) continue // interventions : ni départ ni arrivée liés
      totalMenages += 1
      const prenomAgent = run.assigned_provider_email ? nomAgent(run.assigned_provider_email) : 'personne'
      const droiteMenage = Number.isFinite(b.debut)
        ? `${prenomAgent} · ${formaterHeureCourte(b.debut)}` : prenomAgent
      if (run.status === 'completed') {
        finis += 1; ids.finis.add(b.id)
        listes.finis.push({ id: b.id, nom: nomByLog(run.logement_id, jour), droite: droiteMenage })
      } else {
        // (pas de Set « restants » : le bouton « voir les N restants » a été
        // RETIRÉ — 3e message Dimitri 2026-08-17 : rien entre « pas validés »
        // et l'anneau, qui porte déjà le compte.)
      }
      // LE DÉPART — l'heure réelle du formulaire, sinon le check-out du logement.
      const departStr = guestTimesEffectifs[run.id]?.departure || checkoutTimeByLog(run.logement_id) || null
      const departMin = departStr ? minutesDeHeure(departStr) : null
      if (departMin != null) {
        departs.push(departMin); ids.departs.add(b.id)
        listes.departs.push({ id: b.id, nom: nomByLog(run.logement_id, jour), droite: formaterHeureCourte(departMin), min: departMin })
      }
      // L'ARRIVÉE — réelle sinon check-in par défaut ; NI L'UNE NI L'AUTRE
      // (La parenthèse) → pas de calcul du tout (décision 2026-08-15).
      const arriveeStr = guestTimesEffectifs[run.id]?.arrival || checkinTimeByLog(run.logement_id) || null
      const arriveeMin = arriveeStr ? minutesDeHeure(arriveeStr) : null
      if (arriveeMin == null) continue
      arrivees.push(arriveeMin)
      ids.arrivees.add(b.id)
      listes.arrivees.push({ id: b.id, nom: nomByLog(run.logement_id, jour), droite: formaterHeureCourte(arriveeMin), min: arriveeMin })
      marqueurs[b.id] = arriveeMin
      if (run.status === 'completed') continue
      // ⚠️ DEUX CAS DISTINCTS (Dimitri 2026-08-17) : « pas validé » = le
      // créneau est écoulé sans checklist remplie ; « en danger » = le ménage
      // risque de ne pas être fini avant l'arrivée. Le bandeau dit LEQUEL.
      // (Le sans-agent vit dans « À décider », déjà compté là-bas.)
      const estAujourdhui = positionJour === 'aujourdhui'
      if (estAujourdhui && Number.isFinite(b.fin) && Number.isFinite(nowMin) && b.fin < nowMin) {
        pasValides.push({ id: b.id, nom: nomByLog(run.logement_id, jour), fin: b.fin,
          droite: droiteMenage, min: departMin ?? 9999 })
        ids.pasValide.add(b.id)
        continue
      }
      // ⚠️ ARRIVÉE DÉJÀ PASSÉE = plus un « danger » : le voyageur EST là, et la
      // carte rouge de la frise le crie déjà. Le bloc regarde DEVANT.
      // ⚠️ « MAINTENANT » NE VAUT QU'AUJOURD'HUI (même garde que pasValides) :
      // sur un autre jour, l'heure courante gonflait la fin prévue et le
      // compteur « en danger » mentait — seul le planning fait foi.
      if (estAujourdhui && Number.isFinite(nowMin) && arriveeMin <= nowMin) continue
      const debut = Number.isFinite(b.debut) ? b.debut : (estAujourdhui ? nowMin : NaN)
      const finPrevue = (run.status === 'in_progress' || !estAujourdhui
        ? debut : Math.max(nowMin ?? 0, debut)) + (b.dureeMin || 0)
      if (finPrevue > arriveeMin) {
        dangers.push({ id: run.id, nom: nomByLog(run.logement_id, jour), arrivee: arriveeMin })
        ids.danger.add(b.id)
      }
    }
    departs.sort((a, b2) => a - b2)
    arrivees.sort((a, b2) => a - b2)
    const parNom = (a, b2) => String(a.nom).localeCompare(String(b2.nom), 'fr')
    dangers.sort((a, b2) => (a.arrivee - b2.arrivee) || parNom(a, b2))
    // La SOUS-LIGNE du bloc dit « le plus ancien » — par FIN écoulée, calculé
    // AVANT le tri d'affichage (qui, lui, suit le temps).
    const plusAncienPasValide = pasValides.reduce((m, x) => (m == null || x.fin < m.fin ? x : m), null)
    // LE TRI SUIT LE TEMPS (Dimitri 2026-08-17) : heure croissante — départ
    // pour « départs » et « pas validés », arrivée pour « arrivées » — puis
    // nom de logement à heure égale. « Une liste qu'on lit pour préparer sa
    // journée : elle doit suivre le temps. »
    pasValides.sort((a, b2) => (a.min - b2.min) || parNom(a, b2))
    listes.departs.sort((a, b2) => (a.min - b2.min) || parNom(a, b2))
    listes.arrivees.sort((a, b2) => (a.min - b2.min) || parNom(a, b2))
    listes.finis.sort(parNom)
    return { departs, arrivees, dangers, pasValides, plusAncienPasValide, listes, ids, marqueurs, finis, totalMenages }
  }, [blocsDuJour, guestTimesEffectifs, checkinTimeByLog, checkoutTimeByLog, nomByLog, jour, nowMin, positionJour])

  // Le VOILE de la frise — dérivé du filtre, jamais un second état.
  const lignesEclairees = useMemo(() => {
    if (!filtreJour) return null
    // ⚠️ DEUX BLOCS, DEUX FILTRES (Dimitri 2026-08-17) : « en danger » et
    // « pas validé » sont des états DISTINCTS — le clic sur l'un ne doit
    // jamais éclairer (ni déplier) l'autre.
    const cle = filtreJour.type === 'pas-valide' ? 'pasValide' : filtreJour.type
    return croisementJour.ids[cle] || new Set()
  }, [filtreJour, croisementJour])

  // ⚠️ ctxFrise vit APRÈS le croisement et le voile qu'il transporte — le
  // poser avant, c'était la 7e TDZ de ce fichier (attrapée au lint, 2026-08-15).
  const ctxFrise = useMemo(() => ({
    today, nowMin, jourEstAujourdhui, positionJour, isOrchestrateur, modifiable, assignOptions, self,
    regles, nomAgent, alertIds, estEnRetard, evenementsParRun,
    infoFor, nomByLog, guestTimes: guestTimesEffectifs, idxHoraires, onNavigate,
    checkinTimeByLog, checkoutTimeByLog,
    // Filtre du bandeau (2026-08-15) : voile sur les lignes non concernées, et
    // le TRAIT D'ARRIVÉE (#5c8ab8) seulement sous le filtre « arrivées ».
    lignesEclairees,
    marqueursArrivee: filtreJour?.type === 'arrivees' ? croisementJour.marqueurs : null,
    onContactVoyageur: (isOrchestrateur || isAdmin) ? onContactVoyageur : null,
    onReassign: handleReassign,
    onOpenChecklist: (run) => setChecklistModal({ run }),
    onSignalIncident: (run, eff) => setIncidentModal({ run, effectiveRunStatus: eff }),
    onOuvrirDuree: (bloc) => setModaleDuree({ bloc }),
    onOuvrirRecap: (email) => setRecap({ email }),
    onSupprimerIntervention,
    // ⚠️ LIEN 2 — la bande d'une demande devient TOUCHABLE et ouvre la demande
    // PRÉCISE, pas la liste : rouvrir la liste obligerait à retrouver, parmi N
    // cartes, celle qu'on vient de toucher.
    // ⚠️ LE RETOUR QU'ON CONFIE EMPORTE LE NÔTRE. Sans `retour` à l'intérieur,
    // revenir de l'écran des demandes ramenait sur une frise SANS flèche vers
    // le mois : on était rentré chez soi par la fenêtre.
    onOuvrirDemande: (demande) => onAller && onAller(
      versLaDemande(demande.id, jour, { onglet: ONGLET_FRISE, jour, retour })
    ),
    message: messageFrise,
    // DROITS DE TERRAIN : toujours sur la VRAIE date du jour (naviguer, c'est
    // regarder, pas déverrouiller).
    incidentAutorise: (run) => isRunOpenForMenageToday(run, today) || isWithinIncidentWindow(run),
    statutEffectif: (run) => getEffectiveRunStatus(run, today, nowHHMM, checkoutTimeByLog(run.logement_id)),
    // LOT FRISE CONTRÔLE (additif) : le contrôle le plus récent par run
    // (pastille œil des blocs), l'ouverture du détail lecture seule, et le
    // callback « Contrôler » — null si la prop n'est pas câblée (aucun bouton).
    controleParRun: controlesParRun,
    onOuvrirControle: (c) => setDetailControle(c),
    onControler: onControler || null,
    // PANNEAU DU MÉNAGE (maquette 2026-08-19) : les données et gestes des
    // blocs — défaut de contrôleur par logement, désignation par-ménage
    // (optimiste + rollback), retrait d'assignation (même chemin serveur que
    // le menu, à null), incidents du bloc 5 (pool orchestrateur / RLS admin ;
    // null = batch en échec → bloc absent, jamais un écran cassé).
    controleurDefautParLogement: controleurDefauts,
    onDesignerControleur: handleDesignerControleur,
    onRetirerAssignation: handleRetirerAssignation,
    incidentsPanneau: isOrchestrateur ? incidents : adminIncidents,
  }), [
    today, nowMin, nowHHMM, jourEstAujourdhui, positionJour, isOrchestrateur, modifiable, assignOptions, self,
    regles, nomAgent, alertIds, estEnRetard, evenementsParRun, infoFor, nomByLog, guestTimesEffectifs,
    idxHoraires, onNavigate, handleReassign, checkoutTimeByLog, checkinTimeByLog,
    messageFrise, onSupprimerIntervention, onAller, jour, retour, lignesEclairees, filtreJour, croisementJour,
    isAdmin, onContactVoyageur, controlesParRun, onControler,
    controleurDefauts, handleDesignerControleur, handleRetirerAssignation, incidents, adminIncidents,
  ])


  // « 3 en conflit » remplace le décompte d'un agent : tant qu'il est à deux
  // endroits à la fois, savoir combien de ménages il a « à faire » ne sert à rien.
  const conflitParAgent = useMemo(() => {
    const out = {}
    for (const [email, blocs] of conflitsDuJour.parAgent) out[email] = blocs.length
    return out
  }, [conflitsDuJour])

  const avisParLogement = useMemo(() => {
    const out = {}
    for (const c of avisCartes) out[c.logementId] = { avg: c.avg, cleanliness: c.cleanliness }
    return out
  }, [avisCartes])

  const reper = reperJour(jour, today)

  return (
    <div style={{fontFamily:DS.fontBody,color:C.fonce}}>
      <style>{CSS_PILOTAGE}</style>
      {/* GARDE 5 — RESTE FERMÉE À L'ADMIN, et c'est la mesure anti-double-drain.
          L'admin a déjà SON drain unique (AdminChecklistEditor, côté CONTRÔLE) ;
          en monter un second ici rejouerait le piège ADR-207/208. Les deux vues
          sont de toute façon exclusives, mais on ne s'en remet pas à ça seul —
          `useMenagePhotoUploadQueue` compte désormais ses montages et crie. */}
      {isOrchestrateur && <OrchestrateurPhotoDrain onUploaded={onPhotoUploaded} />}

      {/* ════════ CARTE DE TITRE (lot apparence 2026-08-14 — le doublon navy
          shell/page est mort) ════════ */}
      <CarteTitrePage
        titre="Pilotage de l'équipe"
        sousTitre={<span data-testid="pilotage-sous-titre">{isOrchestrateur
          ? "Vos ménages et votre équipe, en temps réel."
          : "Pilotage de votre conciergerie"}</span>}
        dateTexte={jourLabelLong(jour)}
        sousLeFilet={(() => {
          // ⚠️ VERSION FINALE (maquette Dimitri 2026-08-15) : QUATRE BLOCS, TOUS
          // CLIQUABLES — départs · arrivées · danger (s'il y en a) · avancement
          // en deux boutons. Le clic ÉCLAIRE LA FRISE (même mécanique que le
          // Mois : voile .2, .22s, pas de panneau) ; « arrivées » ajoute le
          // trait #5c8ab8 à l'heure d'arrivée de chaque ligne concernée.
          const { departs, arrivees, dangers, pasValides, plusAncienPasValide, listes, finis, totalMenages } = croisementJour
          const restants = totalMenages - finis
          const heures = (mins) => {
            const uniques = [...new Set(mins)]
            const tetes = uniques.slice(0, 3).map((m) => formaterHeureCourte(m))
            const reste = uniques.length - tetes.length
            if (reste > 0) return `${tetes.join(', ')} et ${reste} autre${reste > 1 ? 's' : ''}`
            if (tetes.length > 1) return `${tetes.slice(0, -1).join(', ')} et ${tetes[tetes.length - 1]}`
            return tetes[0] || ''
          }
          // TOUT VA BIEN — pas de danger, tout validé : la ligne calme.
          if (dangers.length === 0 && restants === 0) {
            return (
              <div data-testid="pilotage-ligne-calme"
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%',
                  flexShrink: 0, background: 'rgba(219,233,226,.9)', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center' }}>
                  <Check size={16} strokeWidth={2.6} color="#1f5a43" />
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff' }}>
                    La journée se déroule bien
                  </span>
                  <span style={{ display: 'block', fontSize: 10.5, marginTop: 1, color: 'rgba(255,255,255,.72)' }}>
                    {totalMenages > 0
                      ? `${departs.length} départ${departs.length > 1 ? 's' : ''}, ${arrivees.length} arrivée${arrivees.length > 1 ? 's' : ''}, tout est fait`
                      : 'rien de prévu aujourd\'hui'}
                  </span>
                </span>
              </div>
            )
          }
          const actif = (type) => filtreJour?.type === type
          const bascule = (type) => setFiltreJour((f) => (f?.type === type ? null : { type }))
          const pastille = (fond) => ({
            width: etroitSpec ? 26 : 30, height: etroitSpec ? 26 : 30,
            borderRadius: etroitSpec ? 7 : 9, background: fond,
            flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' })
          const styleBloc = (type) => ({ display: 'flex', alignItems: 'center', gap: 9,
            background: 'none', border: 'none', width: '100%',
            cursor: 'pointer', textAlign: 'left', padding: 0,
            fontFamily: DS.fontBody })
          // LE BLOC SE DÉPLIE AU CLIC (Dimitri 2026-08-17) : il garde sa
          // place, seule la hauteur du bandeau s'ajuste ; UN SEUL ouvert
          // (c'est l'état du filtre) ; le filtre d'éclairage continue.
          const conteneurBloc = (type) => ({
            display: 'flex', flexDirection: 'column', alignItems: 'stretch',
            padding: etroitSpec ? '6px 9px' : '4px 8px', borderRadius: 9,
            minHeight: etroitSpec ? 44 : undefined,
            // SPEC §2 (étroit) : fond .13 permanent, actif .24 + liseré .32.
            background: etroitSpec
              ? (actif(type) ? 'rgba(255,255,255,.24)' : 'rgba(255,255,255,.13)')
              : (actif(type) ? 'rgba(255,255,255,.1)' : 'none'),
            boxShadow: actif(type)
              ? `inset 0 0 0 1px rgba(255,255,255,${etroitSpec ? '.32' : '.22'})` : 'none',
          })
          // chevron fermé → croix ouvert. Au CLIC seulement — pas de survol.
          const croix = (type) => actif(type)
            ? <X size={14} strokeWidth={2.4} color="rgba(255,255,255,.7)" style={{ flexShrink: 0 }} />
            : <ChevronDown size={14} strokeWidth={2.2} color="rgba(255,255,255,.45)" style={{ flexShrink: 0 }} />
          // TOUTES les lignes — si je clique, c'est que je veux tout voir.
          const listeDepliee = (type, lignes) => actif(type) && lignes.length > 0 && (
            <span data-testid={`pilotage-liste-${type}`}
              style={{ display: 'block', borderTop: '1px solid rgba(255,255,255,.18)',
                marginTop: 8, paddingTop: 7 }}>
              {lignes.map((l) => (
                <span key={l.id} style={{ display: 'flex', justifyContent: 'space-between',
                  gap: 12, fontSize: 10.5, marginBottom: 3, fontFamily: DS.fontBody }}>
                  <span style={{ color: 'rgba(255,255,255,.9)', minWidth: 0, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.nom}</span>
                  <span style={{ color: 'rgba(255,255,255,.62)', flexShrink: 0 }}>{l.droite}</span>
                </span>
              ))}
            </span>
          )
          // Les filets verticaux n'ont pas de sens en PILE (étroit) — ils
          // deviendraient des tirets orphelins entre les blocs.
          const filet = etroitSpec ? null : <span aria-hidden="true" style={{ width: 1, height: 30,
            background: 'rgba(255,255,255,.16)', alignSelf: 'center', flexShrink: 0 }} />
          return (
            <div style={etroitSpec
              ? { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 5 }
              : { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              {/* BLOC 1 — LES DÉPARTS */}
              <div style={conteneurBloc('departs')}>
              <button type="button" data-testid="pilotage-departs" className="fbz-press fbz-cible-tactile"
                aria-pressed={actif('departs')} onClick={() => bascule('departs')}
                style={styleBloc('departs')}>
                <span style={pastille('rgba(255,255,255,.14)')}>
                  <LogOut size={16} strokeWidth={2.2} color="#fff" />
                </span>
                <span>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff' }}>
                    {departs.length} départ{departs.length > 1 ? 's' : ''}
                  </span>
                  <span style={{ display: 'block', fontSize: 10.5, color: 'rgba(255,255,255,.72)' }}>
                    {heures(departs) || '—'}
                  </span>
                </span>
                {croix('departs')}
              </button>
              {listeDepliee('departs', listes.departs)}
              </div>
              {filet}
              {/* BLOC 2 — LES ARRIVÉES (le filtre pose AUSSI le trait #5c8ab8) */}
              <div style={conteneurBloc('arrivees')}>
              <button type="button" data-testid="pilotage-arrivees" className="fbz-press fbz-cible-tactile"
                aria-pressed={actif('arrivees')} onClick={() => bascule('arrivees')}
                style={styleBloc('arrivees')}>
                <span style={pastille('rgba(255,255,255,.14)')}>
                  <LogIn size={16} strokeWidth={2.2} color="#fff" />
                </span>
                <span>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff' }}>
                    {arrivees.length} arrivée{arrivees.length > 1 ? 's' : ''}
                  </span>
                  <span style={{ display: 'block', fontSize: 10.5, color: 'rgba(255,255,255,.72)' }}>
                    {heures(arrivees) || '—'}
                  </span>
                </span>
                {croix('arrivees')}
              </button>
              {listeDepliee('arrivees', listes.arrivees)}
              </div>
              {/* BLOC 3a — LE DANGER (risque de ne pas finir avant l'arrivée). */}
              {dangers.length > 0 && (<>
                {filet}
                <div style={conteneurBloc('danger')}>
                <button type="button" data-testid="pilotage-danger" className="fbz-press fbz-cible-tactile"
                  aria-pressed={actif('danger')} onClick={() => bascule('danger')}
                  style={styleBloc('danger')}>
                  <span style={pastille('rgba(248,220,209,.94)')}>
                    <AlertTriangle size={16} strokeWidth={2.2} color="#8f2f1d" />
                  </span>
                  <span>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#f5a892' }}>
                      {dangers.length} ménage{dangers.length > 1 ? 's' : ''} en danger
                    </span>
                    <span style={{ display: 'block', fontSize: 10.5, color: 'rgba(255,255,255,.72)' }}>
                      {dangers[0].nom} · arrivée à {formaterHeureCourte(dangers[0].arrivee)}
                    </span>
                  </span>
                  {croix('danger')}
                </button>
                {listeDepliee('danger', dangers.map((d) => ({ id: d.id, nom: d.nom, droite: `arrivée ${formaterHeureCourte(d.arrivee)}` })))}
                </div>
              </>)}
              {/* BLOC 3b — LE PAS-VALIDÉ (terminé, checklist pas remplie). */}
              {pasValides.length > 0 && (<>
                {filet}
                <div style={conteneurBloc('pas-valide')}>
                <button type="button" data-testid="pilotage-pas-valide" className="fbz-press fbz-cible-tactile"
                  aria-pressed={actif('pas-valide')} onClick={() => bascule('pas-valide')}
                  style={styleBloc('pas-valide')}>
                  <span style={pastille('rgba(248,220,209,.94)')}>
                    <ClipboardX size={16} strokeWidth={2.2} color="#8f2f1d" />
                  </span>
                  <span>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#f5a892' }}>
                      {pasValides.length} ménage{pasValides.length > 1 ? 's' : ''} pas validé{pasValides.length > 1 ? 's' : ''}
                    </span>
                    <span style={{ display: 'block', fontSize: 10.5, color: 'rgba(255,255,255,.72)' }}>
                      le plus ancien : {plusAncienPasValide?.nom}
                    </span>
                  </span>
                  {croix('pas-valide')}
                </button>
                {listeDepliee('pas-valide', pasValides)}
                </div>
              </>)}
              {filet}
              {/* BLOC 4 — L'ANNEAU, DERNIER ÉLÉMENT du bandeau (2026-08-17),
                  sans filet à sa droite. */}
              <button type="button" data-testid="pilotage-avancement" className="fbz-press fbz-cible-tactile"
                aria-pressed={actif('finis')} onClick={() => bascule('finis')}
                style={{ ...styleBloc('finis'), width: 'auto', flexShrink: 0 }}>
                {/* L'AVANCEMENT EN ANNEAU (Dimitri 2026-08-17) : la part
                    faite en blanc — on voit la journée sans lire. */}
                <span aria-hidden="true" style={{ position: 'relative', width: 34, height: 34,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="34" height="34" viewBox="0 0 34 34" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="17" cy="17" r="14" fill="none" stroke="rgba(255,255,255,.2)" strokeWidth="4" />
                    {/* LISERÉ #1a4f3a sous le vert (Dimitri 2026-08-17) :
                        1.79:1 côté ciel du dégradé — sans lui, l'anneau
                        disparaît selon l'endroit. Même dasharray, trait plus
                        large : le vert garde un contour sombre partout. */}
                    <circle cx="17" cy="17" r="14" fill="none" stroke="#1a4f3a" strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={`${(totalMenages > 0 ? finis / totalMenages : 0) * 87.96} 87.96`} />
                    <circle cx="17" cy="17" r="14" fill="none" stroke="#7fc4a3" strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${(totalMenages > 0 ? finis / totalMenages : 0) * 87.96} 87.96`} />
                  </svg>
                  <span style={{ position: 'absolute', fontSize: 9.5, fontWeight: 700,
                    color: '#fff', fontFamily: DS.fontBody, fontVariantNumeric: 'tabular-nums' }}>
                    {finis}/{totalMenages}
                  </span>
                </span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.75)', fontFamily: DS.fontBody }}>
                  finis
                </span>
                {actif('finis') && <X size={14} strokeWidth={2.4} color="rgba(255,255,255,.7)" style={{ flexShrink: 0 }} />}
                {/* SPEC §2 (étroit) : la jauge porte sa BARRE — 5 px, rayon 3,
                    fond .2, remplie en #7fc4a3. */}
                {etroitSpec && (
                  <span aria-hidden="true" data-testid="pilotage-jauge-barre"
                    style={{ display: 'block', width: '100%', height: 5, borderRadius: 3,
                      background: 'rgba(255,255,255,.2)', marginTop: 6, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', borderRadius: 3,
                      width: `${totalMenages > 0 ? Math.round((finis / totalMenages) * 100) : 0}%`,
                      background: '#7fc4a3' }} />
                  </span>
                )}
              </button>
            </div>
          )
        })()}
        actions={<>
          {/* ⚠️ LA FLÈCHE DE RETOUR N'EXISTE QUE SI L'ON EST ARRIVÉ D'AILLEURS. */}
          {retour && (
            <button type="button" data-testid="suivi-retour" onClick={() => onAller && onAller(retour)}
              aria-label={libelleRetour({ retour })} title={libelleRetour({ retour })}
              className="fbz-press"
              style={{ ...boutonCarteTitre(), width: 40, height: 40, padding: 0, justifyContent: 'center' }}>
              <ArrowLeft size={17} strokeWidth={2.2} />
            </button>
          )}
          {/* ⚠️ « MODIFIER » — ADMIN SEULEMENT. Actif = CORAIL plein : le mode
              d'édition ouvert est une alerte d'état, la seule légitime ici. */}
          {isAdmin && (
            <button type="button" data-testid="pilotage-bouton-modifier"
              onClick={() => { setModeModif(v => !v); setInviteModif(false) }}
              className="fbz-press fbz-cible-tactile"
              style={boutonCarteTitre({ actif: modeModif })}>
              {modeModif ? 'Modification active · Terminer' : 'Modifier'}
            </button>
          )}
          {/* GARDE 6 — OUVERTE (frise « telle qu'elle est », demande Dimitri). */}
          {pilote && (
            <button type="button" onClick={() => setModaleIntervention(true)}
              className="fbz-press fbz-cible-tactile"
              data-testid="pilotage-bouton-intervention"
              style={boutonCarteTitre()}>
              <Plus size={14} strokeWidth={2.4} /> Intervention
            </button>
          )}
          {/* NAVIGATION PAR JOUR — pilules blanches sur la carte ciel pâle. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
            background: '#fff', borderRadius: 10, padding: 3 }}>
            <NavJourBouton label="Jour précédent" onClick={() => setJour(decalerJour(jour, -1))}>
              <ChevronLeft size={17} strokeWidth={2.2} />
            </NavJourBouton>
            <button
              type="button"
              onClick={() => setJour(today)}
              disabled={jourEstAujourdhui}
              title={jourEstAujourdhui ? undefined : "Revenir à aujourd'hui"}
              data-testid="pilotage-jour"
              className="fbz-hitbox44"
              style={{
                minWidth:132,textAlign:'center',background:'transparent',border:'none',
                color:'#2e3d4d',fontFamily:DS.fontDisplay,fontSize:14.5,fontWeight:600,
                cursor: jourEstAujourdhui ? 'default' : 'pointer',padding:'6px 4px',lineHeight:1.25,
              }}>
              {jourLabelCourt(jour)}
              {reper && reper !== "aujourd'hui" && (
                <span style={{display:'block',fontSize:10.5,fontWeight:400,color:'#5c7d99',fontFamily:DS.fontBody}}>{reper}</span>
              )}
            </button>
            <NavJourBouton label="Jour suivant" onClick={() => setJour(decalerJour(jour, 1))}>
              <ChevronRight size={17} strokeWidth={2.2} />
            </NavJourBouton>
          </div>
        </>} />
      {basculePage}

      {reassignMsg && (
        <div style={{margin:'12px 20px 0',display:'inline-flex',gap:7,alignItems:'center',padding:'8px 12px',borderRadius:8,fontSize:12,background: reassignMsg.startsWith('✅') ? DS.successBg : DS.dangerBg,color: reassignMsg.startsWith('✅') ? DS.successText : DS.dangerText}}>
          {reassignMsg.startsWith('✅') ? <CircleCheck size={14} strokeWidth={2} /> : <AlertTriangle size={14} strokeWidth={2} />}
          {reassignMsg.replace(/^[✅❌]\s*/, '')}
        </div>
      )}

      {loading && <div style={{padding:40,textAlign:'center',color:DS.textMuted,fontSize:13}}>Chargement…</div>}
      {error && (
        <div style={{display:'flex',gap:9,alignItems:'flex-start',margin:'14px 20px',padding:14,background:DS.dangerBg,color:DS.dangerText,borderRadius:10,fontSize:13}}>
          <AlertTriangle size={16} strokeWidth={2} style={{flexShrink:0,marginTop:1}} /> {error}
        </div>
      )}

      {!loading && !error && (
        <div className="fbz-pilotage">
          {/* ════════ COLONNE PRINCIPALE ════════ */}
          <main className="fbz-pilotage-main">
            {/* 1 · BILAN DE LA VEILLE — la seule touche de vert. */}
            {!etroitSpec && bilan.affichable && (
              <div data-testid="pilotage-bilan-veille"
                style={{
                  display:'flex',alignItems:'center',gap:9,marginBottom:16,
                  padding:'10px 14px',borderRadius:10,fontSize:12.5,fontWeight:600,
                  background: bilan.tousValides ? C.vertFond : C.orphFond,
                  color: bilan.tousValides ? C.vertTexte : C.retardTexte,
                }}>
                {bilan.tousValides
                  ? <CircleCheck size={15} strokeWidth={2.2} style={{flexShrink:0}} />
                  : <AlertTriangle size={15} strokeWidth={2.2} style={{flexShrink:0}} />}
                <span>{bilan.texte}</span>
              </div>
            )}

            {/* 2 · LA JOURNÉE EN FRISE — deux vues (par logement / par agent). */}
            <FriseJournee
              vue={vueFrise}
              onVue={setVueFrise}
              runs={runsFrise}
              interventions={interventions}
              logementsById={logementsById}
              horaires={horairesParLogement}
              heuresFixees={heuresFixees}
              agentsPool={assignOptions.map(o => o.value)}
              gestes={gestesFrise}
              ctx={ctxFrise}
            />

            {/* 3 · DEMAIN */}
            {demain.nb > 0 && (
              <button
                type="button"
                data-testid="pilotage-demain"
                onClick={() => setJour(lendemain)}
                className="fbz-press"
                style={{
                  marginTop:18,width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',
                  gap:10,background:C.demain,border:'none',borderRadius:12,padding:'13px 16px',
                  cursor:'pointer',fontFamily:DS.fontBody,textAlign:'left',
                }}>
                <span style={{fontSize:13,fontWeight:700,color:C.gris}}>
                  Demain · {demain.nb} ménage{demain.nb > 1 ? 's' : ''}
                </span>
                <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                  {demain.sansAgent > 0 && (
                    <span style={{fontSize:12.5,fontWeight:700,color:C.demainAlerte}}>
                      {demain.sansAgent} sans agent
                    </span>
                  )}
                  <ChevronRight size={16} strokeWidth={2.2} color={C.gris} />
                </span>
              </button>
            )}
          </main>

          {/* ════════ COLONNE DE DROITE ════════ */}
          <aside className="fbz-pilotage-aside">
            {/* 1 · « IL EN FAUT MAINTENANT » (2026-08-21) — les manques
                signalés par les agents. AUJOURD'HUI en terracotta d'abord
                (le voyageur arrive sans), PROCHAINE FOIS en ambre — jamais
                mélangés. ✓ = clôture à la main (un dépôt du produit clôt
                tout seul). */}
            {pilote && manques.length > 0 && (
              <div data-testid="pilotage-manques"
                style={{ background: '#fdf9f7', border: '1px solid #e0b5a5', borderRadius: 12,
                  padding: '11px 12px', marginBottom: 12 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em',
                  textTransform: 'uppercase', color: '#8f2f1d', marginBottom: 7 }}>
                  Il en faut maintenant
                </div>
                {[...manques].sort((a, b) =>
                  (a.echeance === 'aujourdhui' ? 0 : 1) - (b.echeance === 'aujourdhui' ? 0 : 1))
                  .slice(0, 8).map((s) => (
                    <div key={s.id} data-testid={`pilotage-manque-${s.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 4 }}>
                      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%',
                        flexShrink: 0,
                        background: s.echeance === 'aujourdhui' ? '#b8412b' : '#d4a24c' }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 11,
                        color: s.echeance === 'aujourdhui' ? '#8f2f1d' : '#8a5f19',
                        fontWeight: s.echeance === 'aujourdhui' ? 700 : 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {nomByLog(s.logement_id, jour)} — {s.echeance === 'aujourdhui'
                          ? 'il manque pour cette arrivée' : 'il manquera au prochain ménage'} : <strong>{s.cs_id
                          ? nomProduit(cfg, s.cs_id) : s.texte}</strong>
                        <span style={{ color: '#98a2ad', fontWeight: 400 }}> · {s.par_prenom}</span>
                      </span>
                      <button type="button" data-testid={`pilotage-manque-regler-${s.id}`}
                        aria-label="Marquer réglé" title="Marquer réglé"
                        onClick={() => reglerManque(s.id).catch(() => {})}
                        className="fbz-press"
                        style={{ flexShrink: 0, width: 24, height: 24, display: 'inline-flex',
                          alignItems: 'center', justifyContent: 'center', borderRadius: 7,
                          border: '1px solid #c9e0d4', background: '#fff', cursor: 'pointer',
                          color: '#1a4f3a', fontSize: 12, fontWeight: 700 }}>✓</button>
                    </div>
                  ))}
              </div>
            )}

            {/* 2 · À DÉCIDER — LA carte ciel plein (v2) : trois signaux qui
                étaient trois cartes. incidents à juger (le trou comblé) ·
                demandes · sans agent (tout ce que « À assigner » faisait). */}
            {etroitSpec ? (
              /* §6 — LES SECTIONS EN LIGNES (téléphone). CORRECTION Dimitri
                 22/08 : « À décider », « Stock » et « Propreté » faisaient
                 DOUBLON avec la barre du bas (mêmes destinations) — retirées
                 du téléphone. « Cette semaine » et « Hier » restent (pas
                 d'onglet à eux) ; l'ordinateur garde toutes ses cartes. */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* « EN RÉSUMÉ » (GO Dimitri 22/08, téléphone seulement) : la
                    journée ÉCRITE EN TOUTES LETTRES depuis LES BLOCS DE LA
                    FRISE. Des faits, jamais un jugement ; quand tout va bien,
                    on le dit. L'ordinateur garde « Cette semaine ». */}
                <div data-testid="bloc-en-resume"
                  style={{ background: '#fff', border: '1px solid #e3e8ee', borderRadius: 12,
                    padding: '11px 13px' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
                    textTransform: 'uppercase', color: '#5c7d99', marginBottom: 7 }}>
                    En résumé
                  </div>
                  {resumeDuJour.map((ph, i) => (
                    <div key={i} style={{ fontSize: 12, lineHeight: 1.55, color: '#233240',
                      marginTop: i === 0 ? 0 : 5 }}>
                      {ph}
                    </div>
                  ))}
                </div>
                {bilan.affichable && (
                  <LigneSection testId="ligne-hier" fond="#7fc4a3" Icone={CircleCheck}
                    titre="Hier" precision={bilan.texte} sansChevron
                    carteFond={bilan.tousValides ? '#f7fbf9' : '#fdf9f7'}
                    carteBord={bilan.tousValides ? '#c9e0d4' : '#e0b5a5'} />
                )}
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
                  textTransform: 'uppercase', color: '#5c7d99', margin: '6px 0 0' }}>
                  Les règles par défaut
                </div>
              </div>
            ) : (
            <CarteADecider
              incidents={incidentsAJuger}
              pending={horaires.pending}
              controles={controlesATraiter}
              aAssigner={aAssigner}
              jourCourant={jour}
              aujourdHui={today}
              onNavigate={onNavigate}
              onAllerAuJour={setJour} />
            )}

            {/* 3 · LE CONTEXTE, replié. */}
            {/* PLUS DE DÉPLIAGE (Dimitri 2026-08-17) : trois lignes, tout
                visible, tout le temps — le repli répétait la même phrase. */}
            {!etroitSpec && stockBas.length > 0 && <CarteStock produits={stockBas} />}

            {!etroitSpec && <Repliable titre={weekStart === decalerJour(today, -(isoDayOfWeek(today) - 1)) ? 'Cette semaine' : `Semaine du ${jourLabelCourt(weekStart)}`}
              testId="pilotage-repli-semaine">
              {weekByAgent.length === 0 ? (
                <div style={{fontSize:11.5,color:DS.textMuted}}>Aucun ménage.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {orphanWeekRow && (
                    <LigneAgent
                      nom="Non assigné" orphelin testId="pilotage-agent-orphelins"
                      aFaire={orphanWeekRow.aFaire} fait={orphanWeekRow.fait}
                      onOuvrir={() => setRecap({ email: SANS_AGENT })}
                    />
                  )}
                  {agentWeekRows.map(a => (
                    <LigneAgent
                      key={a.email}
                      testId={`pilotage-agent-${a.email}`}
                      // Le NOM, pas le libellé d'option (« Moi » doublerait
                      // le badge) : prénom saisi sinon dérivation.
                      nom={prenomsPool[(a.email || '').toLowerCase()] || libelleDepuisEmail(a.email)}
                      initiales={initialsFromEmail(a.email)}
                      moi={a.email === self}
                      aFaire={a.aFaire} fait={a.fait}
                      conflit={conflitParAgent[a.email] || 0}
                      onOuvrir={() => setRecap({ email: a.email })}
                    />
                  ))}
                </div>
              )}
            </Repliable>}

            {/* 4 · PROPRETÉ — NAVY (le ciel est pris par « À décider »). */}
            {!etroitSpec && <CartePropreté cartes={avisCartes} stats={avisStats} onNavigate={onNavigate} />}

            {/* 5 · RÈGLES — lien discret, après un filet. */}
            {/* GARDE 7 — OUVERTE : la règle par défaut d'un logement est une
                configuration qui appartient à l'admin autant qu'à lui. */}
            {/* ⚠️ MÊME FORME que « Durées par logement » (bug relevé Dimitri
                2026-08-15) : le lien souligné passait pour du texte. */}
            {pilote && (
              <div style={{marginTop:18,paddingTop:14,borderTop:`1px solid ${C.carteBord}`}}>
                <button type="button" onClick={() => setReglesOuvertes(v => !v)}
                  className="fbz-press fbz-cible-tactile"
                  data-testid="pilotage-lien-regles" aria-expanded={reglesOuvertes}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    background: '#f4f9fd', border: 'none', borderRadius: 11,
                    padding: '9px 12px', cursor: 'pointer', textAlign: 'left',
                    fontFamily: DS.fontBody }}>
                  <Users size={15} strokeWidth={2.2} color="#5c6773" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 11, color: '#233240', fontWeight: 600 }}>
                    Qui s'occupe de quoi par défaut
                  </span>
                  {reglesOuvertes
                    ? <ChevronDown size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />
                    : <ChevronRight size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />}
                </button>
              </div>
            )}

            {/* 6 · DURÉES PAR LOGEMENT (maquette 2026-08-15) : l'orchestrateur
                PLANIFIE — il ajuste les durées de base aussi bien que l'admin.
                Écriture via `menage_duree_set` (admin + orchestrateur cloisonné
                logIds, décision 2026-08-10) — le périmètre est le filtre, on
                n'en ajoute pas. Bornes UI 30 min – 5 h (décision 2026-08-15). */}
            {pilote && (
              <div style={{ marginTop: 10 }}>
                <button type="button" data-testid="pilotage-controleurs-titre"
                  onClick={() => setControleursOuverts(v => !v)} aria-expanded={controleursOuverts}
                  className="fbz-press fbz-cible-tactile"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    background: '#f4f9fd', border: 'none', borderRadius: 11,
                    padding: '9px 12px', cursor: 'pointer', textAlign: 'left',
                    fontFamily: DS.fontBody }}>
                  <Eye size={15} strokeWidth={2.2} color="#5c6773" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 11, color: '#233240', fontWeight: 600 }}>
                    Qui contrôle quoi par défaut
                  </span>
                  {controleursOuverts
                    ? <ChevronDown size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />
                    : <ChevronRight size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />}
                </button>
              </div>
            )}

            {pilote && (
              <div style={{ marginTop: 10 }}>
                <button type="button" data-testid="pilotage-durees-titre"
                  onClick={() => setDureesOuvertes(v => !v)} aria-expanded={dureesOuvertes}
                  className="fbz-press fbz-cible-tactile"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    background: '#f4f9fd', border: 'none', borderRadius: 11,
                    padding: '9px 12px', cursor: 'pointer', textAlign: 'left',
                    fontFamily: DS.fontBody }}>
                  <Hourglass size={15} strokeWidth={2.2} color="#5c6773" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 11, color: '#233240', fontWeight: 600 }}>
                    Durées par logement
                  </span>
                  {dureesOuvertes
                    ? <ChevronDown size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />
                    : <ChevronRight size={13} strokeWidth={2.4} color="#c3ccd4" style={{ flexShrink: 0 }} />}
                </button>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* Règles d'assignation — dépliées à la demande, pleine largeur (le
          sélecteur par logement ne tient pas dans 235 px). */}
      {/* Durées par logement — dépliées EN DESSOUS, pleine largeur, comme les
          règles : même position, même façon de pousser le reste. */}
      {dureesOuvertes && pilote && (
        <div style={{ padding: '0 20px 40px' }}>
          <SectionDurees cfg={cfg} currentUser={currentUser} />
        </div>
      )}

      {controleursOuverts && pilote && (
        <div style={{ padding: '0 20px 40px' }}>
          <SectionControleursDefaut
            logements={sesLogements}
            defauts={controleurDefauts}
            prenoms={prenomsPool}
            selfEmail={(currentUser?.email || '').toLowerCase()}
            onChange={() => setVersionDefauts((v) => v + 1)}
          />
        </div>
      )}

      {reglesOuvertes && pilote && (
        <div style={{padding:'0 20px 40px'}}>
          <ReglesAssignationSection
            supabase={supabase}
            logements={sesLogements}
            agents={assignOptions}
            regles={regles}
            onChange={rechargerRegles}
          />
        </div>
      )}

      {recap && recap.email !== SANS_AGENT && (
        <RecapAgent
          email={recap.email}
          blocsSemaine={blocsSemaine.filter(b => b.agent === recap.email)}
          libelleSemaine={libelleSemaine}
          avisParLogement={avisParLogement}
          incidents={incidents}
          estEnRetard={estEnRetard}
          onFermer={() => setRecap(null)}
        />
      )}

      {recap && recap.email === SANS_AGENT && (
        <RecapSansAgent
          blocsSansAgent={blocsDuJour.filter(b => !b.agent)}
          tousLesBlocs={blocsDuJour}
          agents={assignOptions}
          regles={regles}
          nomAgent={(e) => nomAgent(e) || e}
          arriveeDe={(b) => arriveeAffichee({
            arriveeFormulaire: b.run ? guestTimes[b.run.id]?.arrival : null,
            checkinLogement: checkinTimeByLog(b.logementId),
          })}
          onAssigner={async (b, email) => { await handleReassign(b.run, email); setRecap(null) }}
          onAppliquerRegles={async (blocs) => {
            // On applique UNE PAR UNE et on s'arrête au premier refus : enchaîner
            // en avalant les erreurs laisserait croire que tout est passé.
            for (const b of blocs) {
              const cible = regles?.[b.logementId]?.provider_email
              if (!cible) continue
              await handleReassign(b.run, cible)
            }
            setRecap(null)
          }}
          onFermer={() => setRecap(null)}
        />
      )}

      {modaleIntervention && (
        <ModaleIntervention
          logements={sesLogements}
          agents={assignOptions}
          avecPersonnel={isOrchestrateur}
          onFermer={() => setModaleIntervention(false)}
          onValider={async (payload) => {
            setReassignMsg(null)
            const { ok, erreur } = await creerIntervention(supabase, {
              ...payload,
              scheduled_date: jour,
            })
            if (!ok) { setReassignMsg(`❌ ${erreur}`); return }
            await rechargerInterventions()
            setModaleIntervention(false)
          }}
        />
      )}

      {/* LA BULLE DE CONTACT (GO Dimitri 2026-08-17) : ancrée au clic,
          nom + numéro APPELABLE (lien tel:), « Pas de numéro en base » sinon.
          La fermer efface tout — rien ne survit à la bulle. */}
      {contactBulle && (
        <div ref={refBulleContact} data-testid="contact-voyageur-bulle" role="dialog" aria-label="Contact voyageur"
          style={{ position: 'fixed', zIndex: 60,
            left: Math.min(Math.max(8, (contactBulle.ancre?.left ?? 80)), window.innerWidth - 228),
            top: Math.max(8, (contactBulle.ancre?.top ?? 80) - 74),
            width: 220, background: '#233240', color: '#fff', borderRadius: 10,
            boxShadow: '0 6px 22px rgba(0,0,0,.28)', padding: '10px 12px',
            fontFamily: DS.fontBody }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', marginBottom: 2 }}>
                {contactBulle.cote === 'partant' ? 'Voyageur qui part' : 'Voyageur qui arrive'}
              </div>
              {contactBulle.chargement ? (
                <div style={{ fontSize: 12 }}>Consultation…</div>
              ) : contactBulle.erreur ? (
                <div style={{ fontSize: 12, color: '#f5a892' }}>{contactBulle.erreur}</div>
              ) : (
                <>
                  <div style={{ fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {contactBulle.nom || 'Nom inconnu'}
                  </div>
                  {/* TOUT SE LIT ICI, même ce que la carte a dégradé :
                      l'heure et le nombre, toujours. */}
                  {(contactBulle.heure || contactBulle.personnes) && (
                    <div data-testid="contact-voyageur-detail"
                      style={{ marginTop: 2, fontSize: 11.5, color: 'rgba(255,255,255,.78)' }}>
                      {[contactBulle.heure,
                        contactBulle.personnes ? `${contactBulle.personnes} pers.` : null]
                        .filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {contactBulle.telephone ? (
                    <a href={`tel:${contactBulle.telephone}`} data-testid="contact-voyageur-tel"
                      style={{ display: 'inline-block', marginTop: 4, fontSize: 12.5, fontWeight: 700,
                        color: '#9fc5ec', textDecoration: 'none' }}>
                      {contactBulle.telephone}
                    </a>
                  ) : (
                    <div style={{ marginTop: 4, fontSize: 11.5, color: 'rgba(255,255,255,.72)' }}>
                      Pas de numéro en base
                    </div>
                  )}
                </>
              )}
            </div>
            <button type="button" onClick={fermerContact} aria-label="Fermer"
              data-testid="contact-voyageur-fermer"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
              <X size={13} strokeWidth={2.4} color="rgba(255,255,255,.65)" />
            </button>
          </div>
        </div>
      )}
      {modaleDuree && (
        <ModaleDuree
          bloc={modaleDuree.bloc}
          logement={logementsById[modaleDuree.bloc.logementId] || null}
          onFermer={() => setModaleDuree(null)}
          onValider={async ({ minutes, aussiParDefaut }) => {
            const bloc = modaleDuree.bloc
            setReassignMsg(null)
            // DEUX ÉCRITURES DISTINCTES, dans deux endroits différents :
            //   · l'exception de CE ménage → `menage_runs.duree_min` (base) ;
            //   · le défaut du LOGEMENT   → `fbz8-cfg` via l'endpoint serveur.
            // Si la seconde échoue, la première reste vraie : on le dit au lieu
            // d'annoncer un « enregistré » global qui serait faux.
            const { ok, erreur } = await fixerDureeRun(supabase, bloc.id, minutes)
            if (!ok) { setReassignMsg(`❌ ${erreur}`); return }

            if (aussiParDefaut && bloc.logementId) {
              const d = await fixerDureeDefautLogement(bloc.logementId, minutes)
              if (!d.ok) {
                setReassignMsg(`❌ Durée posée pour ce ménage, mais le défaut du logement n'a pas pu être enregistré. ${d.erreur}`)
                await reload()
                setModaleDuree(null)
                return
              }
              setReassignMsg(`✅ Durée du ménage posée, et défaut du logement porté à ${minutes} min.`)
            }
            await reload()
            setModaleDuree(null)
          }}
        />
      )}

      {/* CHANTIER 42 Phase 4 (ADR-302) — CONSIGNES INTERNES, section REPLIABLE
          (fermée par défaut) tout en bas du pilotage. Ne modifie aucune zone
          au-dessus. Le serveur re-vérifie TOUJOURS le scope (pool ∧ logIds
          FRAIS) : ces listes ne servent qu'à proposer des choix sensés. */}
      <ConsignesSection
        currentUser={currentUser}
        logementOptions={consigneLogementOptions}
        agentOptions={consigneAgentOptions}
      />

      {checklistModal && (
        <ChecklistRunModal
          run={checklistModal.run}
          currentUser={currentUser}
          cfg={cfg}
          onClose={() => setChecklistModal(null)}
          onCompleted={handleRunCompleted}
        />
      )}

      {incidentModal && (
        <IncidentModal
          incident={null}
          cfg={cfg}
          data={null}
          /* GARDE 8 — LE VRAI RÔLE. `false` en dur était un défaut, pas un
             choix : monté pour l'admin, il lui cachait le coût et la note
             admin auxquels il a droit. */
          isAdmin={isAdmin}
          userLogIds={[incidentModal.run.logement_id]}
          defaultLogId={incidentModal.run.logement_id}
          effectiveRunStatus={incidentModal.effectiveRunStatus || null}
          run={incidentModal.run}
          onClose={() => setIncidentModal(null)}
          onSave={async (payload) => {
            await createIncident({
              logement_id: payload.logId,
              run_id: incidentModal.run?.id || null,
              source: 'run',
              reported_by: self,
              voyageur: payload.voyageur || null,
              casse_interne: !!payload.casse_interne,
              incident_date: payload.date || null,
              degradations: payload.degradations || [],
              photos: payload.photos || [],
              label: (payload.degradations?.[0]?.type) || 'Incident multi-dégradations',
              description: (payload.degradations || []).map(d => `${d.type} (${d.nombre || 1}) ${d.precision || ''}`.trim()).join(' · '),
              status: STATUS.EN_COURS,
            })
            setIncidentModal(null)
            reload()
          }}
        />
      )}
      {/* LOT FRISE CONTRÔLE — le détail LECTURE SEULE d'un contrôle, ouvert
          par la pastille œil (Échap + clic dehors gérés par le panneau). */}
      {/* La suppression d'une intervention — feuille (tél) / carte (ordi). */}
      {blocASupprimer && (
        <FeuilleConfirmation
          testId="suivi-supprimer-intervention"
          titre={`Supprimer « ${blocASupprimer.nom} » ?`}
          texte="Cette intervention disparaît de la journée et de celle de l'agent. Elle n'est pas archivée : pour la retrouver, il faudra la recréer."
          libelleConfirmer="Supprimer"
          libelleRenoncer="La garder"
          ton="danger"
          onConfirmer={confirmerSuppression}
          onRenoncer={() => setBlocASupprimer(null)} />
      )}

      {detailControle && (
        <PanneauDetailControle
          controleId={detailControle.id}
          onFermer={() => setDetailControle(null)}
        />
      )}
    </div>
  )
}

// ── ConsignesSection (ADR-302) ──────────────────────────────────────
// Enveloppe REPLIABLE (fermée par défaut — la consigne est un acte ponctuel,
// pas une zone de pilotage quotidienne). Aucun état persisté : le pliage est
// local à la session de composant.
function ConsignesSection({ currentUser, logementOptions, agentOptions }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{marginTop:18}}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="fbz-rowhover"
        style={{
          width:'100%',display:'flex',alignItems:'center',gap:10,
          background:DS.card,border:`0.5px solid ${DS.border}`,
          borderRadius: open ? '14px 14px 0 0' : 14,
          borderBottom: open ? 'none' : undefined,
          padding:'12px 14px',cursor:'pointer',fontFamily:DS.fontBody,textAlign:'left',minHeight:48,
        }}>
        <span style={{flex:1,minWidth:0}}>
          <span style={{display:'block',fontFamily:DS.fontDisplay,fontWeight:600,fontSize:14.5,color:DS.marine,lineHeight:1.2}}>
            Consignes internes
          </span>
          <span style={{display:'block',fontSize:11.5,color:DS.textMuted}}>
            Un mot à laisser à un agent, sur un logement — visible de lui seul.
          </span>
        </span>
        {open
          ? <ChevronDown size={18} strokeWidth={2} color={DS.textMuted} style={{flexShrink:0}} />
          : <ChevronRight size={18} strokeWidth={2} color={DS.textMuted} style={{flexShrink:0}} />}
      </button>
      {open && (
        <div style={{border:`0.5px solid ${DS.border}`,borderTop:'none',borderRadius:'0 0 14px 14px',padding:'14px'}}>
          <ConsignesEditor
            currentUser={currentUser}
            logementOptions={logementOptions}
            agentOptions={agentOptions}
            // La forme TRANSVERSE (tous logements) échappe par nature au
            // cloisonnement par logement → ADMIN ONLY (le serveur renvoie 403).
            canWriteTransverse={false}
            // SUPPRESSION DÉFINITIVE : ADMIN SEUL (décision Dimitri). L'orchestrateur
            // archive (réversible), il n'efface jamais. Le serveur le refuserait
            // de toute façon — l'action n'est pas dans son bucket (requireAdmin).
            canDelete={false}
          />
        </div>
      )}
    </div>
  )
}

// ── Bouton de navigation de jour ────────────────────────────────────────────
// Sur la carte de titre ciel pâle (lot apparence) : chevrons navy sur blanc,
// 40px — la cible tactile prime.
function NavJourBouton({ label, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} className="fbz-press"
      style={{width:40,height:40,display:'inline-flex',alignItems:'center',justifyContent:'center',
        borderRadius:8,border:'none',background:'transparent',
        color:'#3d5166',cursor:'pointer',flexShrink:0}}>
      {children}
    </button>
  )
}

// ── Colonne de droite : briques ─────────────────────────────────────────────
function BlocAside({ titre, children }) {
  return (
    <section style={{marginBottom:18}}>
      <h2 style={{fontFamily:DS.fontBody,fontSize:10.5,fontWeight:700,color:DS.textMuted,
        textTransform:'uppercase',letterSpacing:'.09em',margin:'0 0 8px'}}>{titre}</h2>
      {children}
    </section>
  )
}

/**
 * « 11 À ASSIGNER » — le TOTAL en gros, les jours après (consigne Dimitri :
 * le compte doit sauter aux yeux, pas se noyer dans la liste).
 *
 * ⚠️ LA CARTE N'EXISTE QUE S'IL Y A QUELQUE CHOSE. À zéro elle disparaît — une
 * carte vide en permanence serait du bruit, exactement ce qu'on a refusé pour
 * les checklists. Corail « orphelin » existant : même famille de problème que
 * « Cliquer pour assigner », même teinte.
 */
function CarteAAssigner({ lignes, jourCourant, onAllerAuJour }) {
  const parJour = useMemo(() => {
    const par = new Map()
    for (const l of lignes || []) {
      if (!l?.scheduled_date) continue
      par.set(l.scheduled_date, (par.get(l.scheduled_date) || 0) + 1)
    }
    return [...par.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [lignes])
  const total = (lignes || []).length
  if (!total) return null
  return (
    <div data-testid="carte-a-assigner"
      style={{background:C.orphFond,border:`1px solid ${C.orphBord}`,borderRadius:12,
        padding:'13px 14px',marginBottom:14}}>
      <div style={{display:'flex',alignItems:'baseline',gap:7}}>
        <span data-testid="a-assigner-total"
          style={{fontFamily:DS.fontDisplay,fontSize:26,fontWeight:700,lineHeight:1,
            color:C.orphAccent}}>{total}</span>
        <span style={{fontSize:12.5,fontWeight:700,color:C.orphAccent}}>
          à assigner{parJour.length > 1 ? ` · ${parJour.length} jours` : ''}
        </span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:3,marginTop:9}}>
        {parJour.slice(0, 3).map(([date, n]) => (
          <button key={date} type="button" data-testid={`a-assigner-jour-${date}`}
            onClick={() => onAllerAuJour(date)} className="fbz-press"
            style={{display:'flex',alignItems:'center',gap:7,border:'none',
              background: date === jourCourant ? '#fff' : 'transparent',
              borderRadius:7,padding:'4px 7px',cursor:'pointer',textAlign:'left',
              fontFamily:DS.fontBody}}>
            <span style={{fontSize:11.5,color:C.fonce,fontWeight:600,flex:1}}>
              {jourLabelCourt(date)}
            </span>
            <span style={{fontSize:11,fontWeight:700,color:C.orphAccent}}>{n}</span>
          </button>
        ))}
        {parJour.length > 3 && (
          <span style={{fontSize:10.5,color:DS.textMuted,paddingLeft:7}}>
            … et {parJour.length - 3} autre{parJour.length - 3 > 1 ? 's' : ''} jour{parJour.length - 3 > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  )
}

function CarteDemandes({ pending, onNavigate }) {
  const n = Array.isArray(pending) ? pending.length : 0
  const plusAncienne = useMemo(() => demandeLaPlusAncienne(pending), [pending])
  const age = plusAncienne ? ancienneteLisible(plusAncienne.created_at, new Date()) : null
  if (n === 0) return null
  return (
    <BlocAside titre="Demandes voyageurs">
      <div data-testid="pilotage-carte-demandes"
        style={{background:C.ciel,borderRadius:12,padding:'13px 14px',color:C.cielTexte}}>
        <div style={{fontFamily:DS.fontDisplay,fontSize:26,fontWeight:600,lineHeight:1}}>{n}</div>
        <div style={{fontSize:11.5,marginTop:5,lineHeight:1.4}}>
          {n > 1 ? 'demandes en attente' : 'demande en attente'}
          {age && <><br />la plus ancienne depuis {age}</>}
        </div>
        <button type="button" onClick={() => onNavigate && onNavigate('orchestrateur-horaires')}
          className="fbz-press"
          style={{marginTop:10,width:'100%',background:'#fff',color:C.gris,border:'none',
            borderRadius:8,padding:'8px 12px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:DS.fontBody}}>
          Traiter
        </button>
      </div>
    </BlocAside>
  )
}

// §6 (spec mobile 2026-08-22) — UNE LIGNE-SECTION, toutes identiques :
// fond blanc, bord #e3e8ee, r12, padding 10×11 ; pastille carrée 30 r8 icône
// 16 blanche ; titre 12 #233240 gras ; précision 10 UNE ligne ; chevron 16.
function LigneSection({ testId, fond, Icone, titre, precision, onClick,
  carteFond = '#fff', carteBord = '#e3e8ee', sansChevron = false }) {
  const Corps = onClick ? 'button' : 'div'
  return (
    <Corps type={onClick ? 'button' : undefined} data-testid={testId}
      onClick={onClick || undefined} className={onClick ? 'fbz-press' : undefined}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        background: carteFond, border: `1px solid ${carteBord}`, borderRadius: 12,
        padding: '10px 11px', cursor: onClick ? 'pointer' : 'default',
        textAlign: 'left', fontFamily: DS.fontBody }}>
      <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 8,
        background: fond, flexShrink: 0, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center' }}>
        <Icone size={16} strokeWidth={2.2} color="#fff" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#233240' }}>{titre}</span>
        {precision && (
          <span style={{ display: 'block', fontSize: 10, color: '#5c6773', marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{precision}</span>
        )}
      </span>
      {!sansChevron && <ChevronRight size={16} strokeWidth={2.2} color="#98a2ad" style={{ flexShrink: 0 }} />}
    </Corps>
  )
}

function CarteStock({ produits }) {
  const n = Array.isArray(produits) ? produits.length : 0
  if (n === 0) return null
  // ⚠️ LE LANGAGE DU TERRAIN (Dimitri 2026-08-17) : « presque épuisé », jamais
  // « seuil » ni « bientôt » — et un CONSTAT PUR : pas d'action, pas de clic,
  // pas de dépliage (trois lignes, tout visible tout le temps). TOUS les
  // noms — si la liste dépasse un jour cinq produits, le signaler à Dimitri
  // avant d'inventer une troncature.
  const noms = produits.map(p => p?.nom).filter(Boolean)
  return (
    <BlocAside titre="Stock">
      <div data-testid="pilotage-carte-stock"
        style={{width:'100%',textAlign:'left',background:C.orphFond,border:`1px solid ${C.orphBord}`,
          borderRadius:12,padding:'12px 13px',fontFamily:DS.fontBody}}>
        <div style={{display:'flex',alignItems:'center',gap:7,fontSize:12.5,fontWeight:700,color:C.retardTexte}}>
          <Package size={14} strokeWidth={2.2} style={{flexShrink:0}} />
          {n} produit{n > 1 ? 's' : ''} presque épuisé{n > 1 ? 's' : ''}
        </div>
        {noms.length > 0 && (
          <div style={{fontSize:11,color:DS.textSecondary,marginTop:5,lineHeight:1.45}}>
            {noms.join(', ')}
          </div>
        )}
      </div>
    </BlocAside>
  )
}

function LigneAgent({ nom, initiales, moi = false, orphelin = false, aFaire, fait, conflit = 0, testId, onOuvrir }) {
  return (
    <button type="button" onClick={onOuvrir} data-testid={testId} style={{display:'flex',alignItems:'center',gap:8,background: orphelin ? C.orphFond : '#fff',
      border:`1px solid ${orphelin ? C.orphBord : C.carteBord}`,borderRadius:9,padding:'8px 10px',
      width:'100%',textAlign:'left',cursor:'pointer',fontFamily:DS.fontBody}}>
      <span style={{width:24,height:24,borderRadius:7,flexShrink:0,display:'inline-flex',
        alignItems:'center',justifyContent:'center',fontFamily:DS.fontDisplay,fontWeight:600,fontSize:10,
        background: orphelin ? '#fff' : C.cielPale, color: orphelin ? C.orphAccent : C.gris}}>
        {orphelin ? '⚠' : initiales}
      </span>
      <span style={{flex:1,minWidth:0}}>
        <span style={{display:'flex',alignItems:'center',gap:5}}>
          <span style={{fontSize:12,fontWeight:700,color: orphelin ? C.retardTexte : C.gris,
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{nom}</span>
          {moi && <span style={{fontSize:9,fontWeight:700,background:C.cielPale,color:C.gris,padding:'1px 5px',borderRadius:5,flexShrink:0}}>Moi</span>}
        </span>
        {/* ⚠️ LE CONFLIT REMPLACE LE DÉCOMPTE, il ne s'ajoute pas à côté. Un
            agent qui est à deux endroits à la fois a un problème d'emploi du
            temps ; « 3 à faire · 1 fait » à côté du mot « conflit » invite à
            lire la ligne comme une journée normale un peu chargée. */}
        {conflit > 0 ? (
          <span data-testid={`${testId}-conflit`}
            style={{display:'block',fontSize:10.5,marginTop:1,fontWeight:700,color:C.demainAlerte}}>
            {conflit} en conflit
          </span>
        ) : (
          <span style={{display:'block',fontSize:10.5,color:DS.textMuted,marginTop:1,fontVariantNumeric:'tabular-nums'}}>
            <span style={{color: aFaire === 0 ? C.zeroGris : undefined}}>{aFaire} à faire</span>
            {' · '}
            <span style={{color: fait === 0 ? C.zeroGris : undefined}}>{fait} fait{fait > 1 ? 's' : ''}</span>
          </span>
        )}
      </span>
    </button>
  )
}

// ── Carte PROPRETÉ (avis) ───────────────────────────────────────────────────
// ── DURÉES PAR LOGEMENT (maquette 2026-08-15, corrigée le jour même) ─────────
// Le CONTENU se déplie EN DESSOUS de la colonne, pleine largeur — exactement
// comme les règles d'assignation (comprimé dans les 235 px de la colonne, il
// était illisible). L'en-tête, lui, reste dans la colonne (bouton du parent).
// Pas de 15 en 15 minutes, bornes 30 min–5 h, MODIFICATION IMMÉDIATE
// (optimiste, rollback si le serveur refuse). Une exception sur un ménage
// précis se règle dans la frise — le pied le dit.
const DUREE_PAS_MIN = 15
const DUREE_BORNE_BASSE = 30
const DUREE_BORNE_HAUTE = 300

function SectionDurees({ cfg, currentUser }) {
  const [surcharges, setSurcharges] = useState({})   // {logId: minutes} — optimiste
  const [erreur, setErreur] = useState(null)
  const [enVol, setEnVol] = useState({})             // {logId: true} anti double-clic

  const estAdmin = currentUser?.role === 'admin'
  const logIds = currentUser?.logIds
  const lignes = useMemo(() => {
    const out = []
    for (const prop of cfg?.props || []) {
      for (const log of prop.logs || []) {
        const feuilles = (log.chambres && log.chambres.length) ? log.chambres : [log]
        for (const f of feuilles) {
          if (!estAdmin && !(logIds || []).includes(f.id)) continue
          const propre = Number(f.menage_duree_min)
          const parent = Number(log.menage_duree_min)
          const duree = Number.isFinite(propre) && propre > 0 ? propre
            : Number.isFinite(parent) && parent > 0 ? parent : 90
          out.push({ id: f.id, nom: f.nom || f.id, duree })
        }
      }
    }
    return out.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
  }, [cfg, estAdmin, logIds])

  const dureeDe = (l) => surcharges[l.id] ?? l.duree

  const ajuster = async (l, sens) => {
    if (enVol[l.id]) return
    const actuelle = dureeDe(l)
    const voulu = Math.min(DUREE_BORNE_HAUTE, Math.max(DUREE_BORNE_BASSE, actuelle + sens * DUREE_PAS_MIN))
    if (voulu === actuelle) return
    setErreur(null)
    setSurcharges((s) => ({ ...s, [l.id]: voulu }))
    setEnVol((s) => ({ ...s, [l.id]: true }))
    const r = await fixerDureeDefautLogement(l.id, voulu)
    setEnVol((s) => { const n = { ...s }; delete n[l.id]; return n })
    if (!r.ok) {
      // rollback : l'écran ne garde JAMAIS un réglage que le serveur a refusé
      setSurcharges((s) => { const n = { ...s }; delete n[l.id]; return n })
      setErreur(r.erreur || 'Modification refusée.')
    } else if (typeof r.dureeEffective === 'number') {
      setSurcharges((s) => ({ ...s, [l.id]: r.dureeEffective }))
    }
  }

  const boutonPas = (l, sens, signe) => (
    <button type="button" onClick={() => ajuster(l, sens)}
      data-testid={`durees-${sens > 0 ? 'plus' : 'moins'}-${l.id}`}
      aria-label={`${signe} ${DUREE_PAS_MIN} minutes pour ${l.nom}`}
      disabled={!!enVol[l.id]}
      className="fbz-press"
      style={{ width: 26, height: 26, border: '1px solid #c3d5e4', background: '#fff',
        borderRadius: 7, cursor: 'pointer', fontSize: 14, color: '#233240',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: DS.fontBody, flexShrink: 0, opacity: enVol[l.id] ? .5 : 1 }}>
      {signe}
    </button>
  )

  if (!lignes.length) return null
  return (
    <section data-testid="pilotage-durees"
      style={{ background: '#fff', border: '1px solid #d5e3ee', borderRadius: 11, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f4f9fd',
        padding: '10px 14px' }}>
        <Hourglass size={15} strokeWidth={2.2} color="#5c6773" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: '#233240', fontWeight: 700, fontFamily: DS.fontBody }}>
          Durées par logement
        </span>
      </div>
      <div style={{ padding: '4px 14px 10px' }}>
        {erreur && (
          <div data-testid="pilotage-durees-erreur"
            style={{ fontSize: 10.5, color: '#8f2f1d', padding: '6px 0' }}>{erreur}</div>
        )}
        {lignes.map((l, i) => (
          // ⚠️ DEUX ÉTAGES PLUTÔT QU'UN ÉCRASEMENT : le nom occupe sa ligne et
          // les boutons passent dessous quand la place manque (flex-wrap — le
          // nom exige 180 px, le groupe de boutons ne se comprime jamais).
          <div key={l.id} data-testid={`durees-ligne-${l.id}`}
            style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 10px',
              padding: '8px 0', borderTop: i > 0 ? '1px solid #eef1f4' : 'none' }}>
            <span style={{ flex: '1 1 180px', minWidth: 0, fontSize: 12, color: '#233240',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.nom}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              {boutonPas(l, -1, '−')}
              <span data-testid={`durees-valeur-${l.id}`}
                style={{ minWidth: 42, textAlign: 'center', fontSize: 11.5, color: '#233240',
                  fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {formaterDuree(dureeDe(l))}
              </span>
              {boutonPas(l, +1, '+')}
            </span>
          </div>
        ))}
        <div style={{ fontSize: 10, color: '#98a2ad', paddingTop: 8, borderTop: '1px solid #eef1f4' }}>
          Le temps prévu par défaut. Une exception sur un ménage précis se règle dans la frise.
        </div>
      </div>
    </section>
  )
}

function CartePropreté({ cartes, stats, onNavigate }) {
  if (!stats || stats.count === 0) return null

  // Moyenne de propreté sur les logements du périmètre — c'est la note qui
  // concerne l'équipe, pas la note globale du séjour.
  const avecProprete = (cartes || []).filter(c => c.cleanliness != null)
  const proprete = avecProprete.length
    ? Math.round((avecProprete.reduce((s, c) => s + c.cleanliness, 0) / avecProprete.length) * 10) / 10
    : (stats.avg != null ? Number(stats.avg) : null)
  const aSignaler = (cartes || []).filter(c => c.cleanliness != null && c.cleanliness < 4)

  return (
    <BlocAside titre="Propreté">
      <button type="button" onClick={() => onNavigate && onNavigate('menage-avis')} className="fbz-press"
        data-testid="pilotage-carte-proprete"
        style={{position:'relative',overflow:'hidden',width:'100%',textAlign:'left',background:C.gris,
          border:'none',borderRadius:12,padding:'13px 14px',cursor:'pointer',fontFamily:DS.fontBody}}>
        <div style={{position:'relative'}}>
          <div style={{display:'flex',alignItems:'baseline',gap:7}}>
            <span style={{fontFamily:DS.fontDisplay,fontSize:24,fontWeight:600,color:'#fff',lineHeight:1}}>
              {proprete != null ? proprete.toFixed(1).replace('.', ',') : '—'}
            </span>
            <Star size={13} strokeWidth={2.2} color={C.ambre} fill={C.ambre} />
          </div>
          <div style={{fontSize:11.5,color:C.ciel,marginTop:5}}>{stats.count} avis</div>
          <div style={{fontSize:11,color:'rgba(255,255,255,.78)',marginTop:6,lineHeight:1.4}}>
            {aSignaler.length === 0
              ? 'Rien à signaler'
              : `${aSignaler.length} logement${aSignaler.length > 1 ? 's' : ''} sous 4/5`}
          </div>
        </div>
      </button>
    </BlocAside>
  )
}

// ── À DÉCIDER — LA carte ciel plein (maquette v2, 2026-08-15) ────────────────
//
// TROIS SIGNAUX qui étaient trois cartes : la colonne passe de sept blocs à
// cinq. Chaque ligne est CLIQUABLE et mène au geste ; une ligne à zéro
// disparaît ; la carte entière disparaît si les trois sont à zéro.
// ⚠️ RIEN N'EST PERDU de « À assigner » : le total (fenêtre 14 j), les trois
// jours les plus proches, le clic qui fait sauter la frise au jour.
function CarteADecider({ incidents, pending, controles = 0, aAssigner, jourCourant, aujourdHui, onNavigate, onAllerAuJour }) {
  // ⚠️ TROIS LIGNES SIMPLES (correction Dimitri : la première version collait
  // chiffres, mots et dates — illisible). Une ligne = [pastille 21] [libellé
  // COURT] [ancienneté terracotta] — RIEN d'autre. Le détail des jours sans
  // agent s'ouvre AU CLIC, pas dans la carte.
  const [joursOuverts, setJoursOuverts] = useState(false)
  const nbIncidents = Array.isArray(incidents) ? incidents.length : 0
  const nbDemandes = Array.isArray(pending) ? pending.length : 0
  const lignesAssigner = Array.isArray(aAssigner) ? aAssigner : []
  const nbAssigner = lignesAssigner.length
  // ⚠️ MÊME COMPTE QUE LE BADGE (correction Dimitri 2026-08-22) : le badge
  // « À décider » additionne demandes + incidents + contrôles — un contrôle
  // à traiter absent de cette carte faisait dire 3 au badge et 2 à l'écran.
  const nbControles = Number(controles) || 0
  if (!nbIncidents && !nbDemandes && !nbAssigner && !nbControles) return null

  const joursIncident = nbIncidents
    ? Math.max(...incidents.map((i) => joursDAttente(i, aujourdHui) ?? 0))
    : null
  const plusAncienne = nbDemandes ? demandeLaPlusAncienne(pending) : null
  const joursDemande = plusAncienne?.created_at
    ? Math.max(0, Math.floor((Date.now() - Date.parse(plusAncienne.created_at)) / 86400000))
    : null

  const parJour = (() => {
    const m = new Map()
    for (const l of lignesAssigner) {
      if (l?.scheduled_date) m.set(l.scheduled_date, (m.get(l.scheduled_date) || 0) + 1)
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b))
  })()

  const SEP = 'rgba(28,40,51,.14)'
  const ligne = (avecFilet) => ({ display: 'flex', alignItems: 'center', gap: 8,
    width: '100%', background: 'none', border: 'none', padding: '10px 2px',
    borderBottom: avecFilet ? `1px solid ${SEP}` : 'none',
    cursor: 'pointer', textAlign: 'left', fontFamily: DS.fontBody })
  const pastille = { minWidth: 21, height: 21, borderRadius: 7, background: '#fff',
    color: '#1c2833', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 11.5, fontWeight: 700, flexShrink: 0,
    padding: '0 5px', fontVariantNumeric: 'tabular-nums' }
  const libelle = { fontSize: 11, color: '#1c2833', fontWeight: 600 }
  const anciennete = (j) => (j != null && j > 0 ? (
    <span style={{ marginLeft: 'auto', background: '#b8412b', color: '#fff',
      fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '3px 8px',
      flexShrink: 0 }}>{j} j</span>
  ) : null)

  // L'ordre des filets : chaque ligne en porte un SAUF la dernière visible.
  const visibles = [nbIncidents > 0, nbDemandes > 0, nbControles > 0, nbAssigner > 0]
  const derniereVisible = visibles.lastIndexOf(true)

  return (
    <section data-testid="carte-a-decider"
      style={{ background: '#87b8e6', borderRadius: 12, padding: '12px 13px', marginBottom: 14 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: '#22303d',
        textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: 2 }}>
        À décider
      </div>

      {nbIncidents > 0 && (
        <button type="button" data-testid="a-decider-incidents" className="fbz-press fbz-cible-tactile"
          onClick={() => onNavigate && onNavigate('incidents')}
          style={ligne(derniereVisible > 0)}>
          <span style={pastille}>{nbIncidents}</span>
          <span style={libelle}>incident{nbIncidents > 1 ? 's' : ''}</span>
          {anciennete(joursIncident)}
        </button>
      )}

      {nbDemandes > 0 && (
        <button type="button" data-testid="a-decider-demandes" className="fbz-press fbz-cible-tactile"
          onClick={() => onNavigate && onNavigate('orchestrateur-horaires')}
          style={ligne(derniereVisible > 1)}>
          <span style={pastille}>{nbDemandes}</span>
          <span style={libelle}>demande{nbDemandes > 1 ? 's' : ''}</span>
          {anciennete(joursDemande)}
        </button>
      )}

      {nbControles > 0 && (
        <button type="button" data-testid="a-decider-controles" className="fbz-press fbz-cible-tactile"
          onClick={() => onNavigate && onNavigate('menage-controle')}
          style={ligne(derniereVisible > 2)}>
          <span style={pastille}>{nbControles}</span>
          <span style={libelle}>contrôle{nbControles > 1 ? 's' : ''} à traiter</span>
        </button>
      )}

      {nbAssigner > 0 && (
        <>
          <button type="button" data-testid="a-decider-sans-agent" className="fbz-press fbz-cible-tactile"
            onClick={() => setJoursOuverts((v) => !v)}
            aria-expanded={joursOuverts}
            style={ligne(false)}>
            <span style={pastille}>{nbAssigner}</span>
            <span style={libelle}>sans agent</span>
            {joursOuverts
              ? <ChevronDown size={13} strokeWidth={2.4} color="#22303d" style={{ marginLeft: 'auto', flexShrink: 0 }} />
              : <ChevronRight size={13} strokeWidth={2.4} color="#22303d" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
          </button>
          {/* LE DÉTAIL S'OUVRE AU CLIC : les trois jours les plus proches, le
              clic fait sauter la frise au jour (l'héritage d'« À assigner »). */}
          {joursOuverts && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingBottom: 2 }}>
              {parJour.slice(0, 3).map(([date, n]) => (
                <button key={date} type="button" data-testid={`a-assigner-jour-${date}`}
                  onClick={() => onAllerAuJour(date)} className="fbz-press fbz-cible-tactile"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none',
                    background: date === jourCourant ? '#fff' : 'rgba(255,255,255,.55)',
                    borderRadius: 7, padding: '5px 8px', cursor: 'pointer', textAlign: 'left',
                    fontFamily: DS.fontBody }}>
                  <span style={{ fontSize: 11, color: '#1c2833', fontWeight: 600, flex: 1 }}>
                    {jourLabelCourt(date)}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#1c2833' }}>{n}</span>
                </button>
              ))}
              {parJour.length > 3 && (
                <span style={{ fontSize: 10, color: '#22303d', paddingLeft: 8 }}>
                  … et {parJour.length - 3} autre{parJour.length - 3 > 1 ? 's' : ''} jour{parJour.length - 3 > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}

// ── LE CONTEXTE REPLIÉ (v2) — un titre cliquable, le contenu à la demande ────
function Repliable({ titre, children, testId, masque = false }) {
  const [ouvert, setOuvert] = useState(false)
  if (masque) return null
  return (
    <section data-testid={testId} style={{ marginBottom: 12 }}>
      <button type="button" onClick={() => setOuvert((v) => !v)} className="fbz-press"
        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: '#f4f9fd', border: 'none', borderRadius: 10, minHeight: 44,
          padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
          fontFamily: DS.fontBody }}>
        <span style={{ flex: 1, fontSize: 11.5, fontWeight: 700, color: '#3d5166' }}>{titre}</span>
        {ouvert
          ? <ChevronDown size={14} strokeWidth={2.4} color="#c3ccd4" />
          : <ChevronRight size={14} strokeWidth={2.4} color="#c3ccd4" />}
      </button>
      {ouvert && <div style={{ marginTop: 8 }}>{children}</div>}
    </section>
  )
}
